import { defineStore, storeToRefs } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestore } from '@/firebase'
import {
  bridgeCoverageProjectionSchema,
  bridgePatientProjectionSchema,
  consultationSchema,
  STRIPE_CHECKOUT_PROJECTION_KEY,
  STRIPE_SETTLEMENT_PAYMENT_CHECKOUT_PROJECTION_KEY,
  stripeCheckoutProjectionSchema,
  CONSULTANT_LICENSED_STATES_CACHE_DOC_ID,
  consultantLicensedStatesCacheSchema,
  clinicalBookingSupportedStateCodes,
  hasOverlappingClinicalDraftSelect,
  type AvailabilitySlot,
  type BridgeCoverageProjection,
  type BridgePatientResponsibilityBreakdown,
  type Consultation,
  type ConsultationConsultType,
  type ConsultationId,
  type CancellationReason,
  CustomerGender,
  type CustomerId,
  type BridgePolicyId,
  remainingClinicalHoldMinutes,
  EXISTING_CONSULTATION_GATE_OCCUPYING_LIFECYCLE_STATUSES,
  isActiveBookingOccupyingClinicalConsultation,
} from '@gh/common/models'
import { setBookingTrackingConsultationId } from '@/components/consultation/bookingTracking/bookingTrackingService'
import { formatClinicalCheckoutHoldMessage } from '@/components/consultation/clinicalBookingFormat'
import { isStripeCheckoutProjectionPaid } from '@/stripe/clinicalStripeCheckoutProjection'
import { waitForBridgeCoverageReady } from '@/bridge/bridgeCoverageProjectionWait'
import { bridgePolicyPayerDisplayName } from '@/bridge/bridgePolicyDisplay'
import { subscribeBridgePolicy } from '@/bridge/bridgePolicySubscription'
import { waitForClinicalVendorPatientsReady } from '@/bridge/clinicalVendorPatientProjectionWait'
import { prefetchBookableProviderEligibility } from '@/bridge/composables/useBookableProviderEligibilityCalendar'
import {
  fetchBridgePatientProjection,
  fetchBridgePolicyOnFileDisplay,
  fetchBridgePolicyPayerDisplayName,
  INSURANCE_ON_FILE_CONFIRM_FAILED_MESSAGE,
  readReusableBridgePolicyFromProjection,
  tryReuseExistingBridgePolicy,
} from '@/bridge/bridgePolicyReuse'
import {
  invalidateReusableBridgePolicyPrefetch,
  prefetchReusableBridgePolicyCoalesced,
} from '@/bridge/prefetchReusableBridgePolicyCoalesced'
import { prefetchFollowupBookingContextCoalesced } from '@/components/consultation/prefetchFollowupBookingContextCoalesced'
import { resetClinicalBookingClientCaches } from '@/components/consultation/resetClinicalBookingClientCaches'
import {
  BridgeCoverageFailureError,
  BridgeCoverageWaitTimeoutError,
} from '@/bridge/bridgeCoverageErrors'
import { platformCoordApi } from '@/components/cloudFunctions/platformCoord'
import { useCustomerConsultationsHubStore } from '@/stores/customerConsultationsHub'
import { useClinicalTellescopeAvailabilityStore } from '@/stores/clinicalTellescopeAvailability'
import { useToastStore } from '@/stores/toast'
import createLogger from '@/utils/logger'
import {
  toUsStateCode,
  getTimezoneForStateName,
  isBridgeConsentFreshForMaximumConsultationDate,
} from '@gh/common/utils'
import { customerBridgeProfileDemographicsEqual } from '@gh/common/models'
import { STATE_ABBREVIATIONS } from '@/utils/usStates'
import {
  buildConfirmBookingPatch,
  buildReservedConsultationPatch,
  checkoutExpiresAtFromConsultation,
  clearClinicalCheckoutSubmitted,
  clinicalBookingInvalidatedUserMessage,
  consultationSnapshotFieldsToDates,
  insuranceCategoryBillingPath,
  isClinicalBookingStateSupported,
  isClinicalCreateBlockedError,
  isClinicalBookingInvalidatedError,
  isClinicalSlotTakenError,
  isClinicalReserveContinuedInAnotherWindowError,
  isClinicalCheckoutRecentlySubmitted,
  isConflictingScheduledConsultation,
  pickGateOccupyingConsultation,
  isConsultationLifecycleStale,
  isPaymentComplete,
  isSettlementPaymentComplete,
  markClinicalCheckoutSubmitted,
  mergeConsultation,
  persistClinicalInsuranceDisplay,
  restoreClinicalInsuranceDisplay,
  resolveClinicalBookingDisplayStep,
  resolveCashPayEligibleReason,
  resolveConsultationIdForReserve,
  resolveDraftSubPhase,
  isProductPreflowStep,
  shouldAttemptClinicalRelease,
  shouldClearOptimisticPatch,
  slotSelectionFromConsultation,
  formatClinicalAccountScheduledSummary,
  type ClinicalAccountScheduledSummary,
  type ClinicalBillingPath,
  type ClinicalBookingStep,
  type ClinicalCashPayEligibleReason,
  type ClinicalCustomerData,
  type ClinicalDraftSubPhase,
  type ClinicalInsuranceCategory,
  type ClinicalInsuranceDetailsEntry,
  type ClinicalInsuredPaymentFailureReason,
  type ClinicalProductStep,
  type ClinicalProfileDraft,
  type ClinicalReservedSubPhase,
  resolveReservedSubPhaseForResume,
  shouldAutoConfirmReservedSelfPayOnResume,
} from './clinicalConsultationBookingFlow'
import * as Sentry from '@sentry/vue'

const logger = createLogger('stores/clinicalConsultationBooking.ts')

async function callPlatformCoord<T>(
  method: string,
  args: unknown,
  call: () => Promise<T>,
): Promise<T> {
  logger.info(`platformCoord.${method}`, { args })
  try {
    const result = await call()
    logger.info(`platformCoord.${method} ok`, { result })
    return result
  } catch (error) {
    logger.error(`platformCoord.${method} failed`, { args, error })
    throw error
  }
}

const CUSTOMER_CHECKOUT_PROPAGATION_WAIT_MS = 30_000

type ConfirmBookingOptions = {
  billingPath?: 'self_pay'
}

export type ClinicalBookingEntrySource = 'account' | 'signup'

export type StartClinicalBookingSessionArgs = {
  customer: ClinicalCustomerData
  /** Prefer this consultation when loading / reserving. */
  consultationId?: string | null
  /** When set, locks consult type and skips type sub-UI. */
  consultType?: ConsultationConsultType | null
  /** Distinguishes signup vs account booking for layout, exit targets, and session recovery. */
  entrySource?: ClinicalBookingEntrySource
  /** Restore in-memory product preflow step after reload when no consultation exists yet. */
  initialProductStep?: ClinicalProductStep | null
  /** Skip scheduled-consultation occupancy probe for a deliberate fresh booking entry. */
  freshClinicalBooking?: boolean
}

type CancelHoldSnapshot = {
  consultationId: string
  status: Consultation['lifecycleStatus'] | undefined
  customerCheckoutSubmitted: boolean
  reason: CancellationReason
}

export const useClinicalConsultationBookingStore = defineStore(
  'clinicalConsultationBooking',
  () => {
    const toastStore = useToastStore()
    const availabilityStore = useClinicalTellescopeAvailabilityStore()

    async function invokePlatformCoord<T>(
      method: string,
      args: unknown,
      call: () => Promise<T>,
      options?: { skipInvalidatedHandling?: boolean },
    ): Promise<T> {
      try {
        return await callPlatformCoord(method, args, call)
      } catch (error) {
        if (!options?.skipInvalidatedHandling && isClinicalBookingInvalidatedError(error)) {
          await handleClinicalBookingInvalidated(error)
        }
        throw error
      }
    }

    function reportClinicalResumeHardError(cause: unknown): void {
      toastStore.error('Unable to resume your booking. Please try again or contact support.')
      Sentry.captureException(cause instanceof Error ? cause : new Error(String(cause)), {
        tags: { 'clinical.booking': 'resume_hard_error' },
      })
    }

    async function handleClinicalBookingInvalidated(error: unknown): Promise<void> {
      toastStore.error(clinicalBookingInvalidatedUserMessage(error))
      await resetToStartOverAfterInvalidation()
    }

    function toastUnlessClinicalBookingInvalidated(err: unknown, fallbackMessage: string): void {
      if (isClinicalBookingInvalidatedError(err)) {
        return
      }
      toastStore.error(err instanceof Error ? err.message : fallbackMessage)
    }

    async function resetToStartOverAfterInvalidation(): Promise<void> {
      const gen = ++operationGeneration
      const keptCustomer = customer.value
      activeSessionKey = null

      unsubscribeConsultationSnapshot()
      serverConsultation.value = null
      optimisticConsultationPatch.value = null
      authoritativeServerLifecycleStatus.value = null
      consultationId.value = null
      preferredConsultationId.value = null
      sessionConsultType.value = null
      consultTypeChosen.value = null
      customerCheckoutAcknowledged.value = false
      clearCheckoutPropagationState()
      resetLocalPicks()
      cancelledAppointmentSummary.value = null
      error.value = null

      if (gen !== operationGeneration || !keptCustomer) {
        return
      }

      customer.value = keptCustomer
      await beginCustomerProductPreflow()
    }

    async function resumeConsultationAfterResolve(
      resolvedId: string,
      consultTypeHint?: ConsultationConsultType,
      options?: { rejectSelfPayBillingPath?: boolean },
    ): Promise<void> {
      preferredConsultationId.value = resolvedId
      if (consultTypeHint) {
        consultTypeChosen.value = consultTypeHint
      }
      subscribeConsultation(resolvedId)
      const loaded = await waitForInitialConsultationSnapshot(resolvedId, operationGeneration)
      if (options?.rejectSelfPayBillingPath && loaded?.billingPath === 'self_pay') {
        // TODO(bridge-policy-attach): upgrade self_pay draft → insured when attach gates pass.
        const message =
          'You have an in-progress self-pay booking. Cancel it or contact support to switch to insurance.'
        error.value = message
        toastStore.error(message)
        throw new Error(message)
      }
      if (loaded?.billingPath) {
        billingPath.value = loaded.billingPath
      }
      if (loaded?.consultType && !consultTypeChosen.value) {
        consultTypeChosen.value = loaded.consultType
      }
      reservedSubPhase.value = resolveReservedSubPhaseForResume({
        billingPath: loaded?.billingPath ?? billingPath.value,
        lifecycleStatus: loaded?.lifecycleStatus,
        insuredCoverageAcknowledged: false,
      })
      productPreflowComplete.value = true
      productStep.value = null
      if (
        loaded &&
        shouldAutoConfirmReservedSelfPayOnResume({
          lifecycleStatus: loaded.lifecycleStatus,
          billingPath: loaded.billingPath ?? billingPath.value,
        })
      ) {
        reservedSubPhase.value = null
        await confirmBooking()
      }
      const consultType = consultTypeChosen.value ?? loaded?.consultType ?? null
      if (consultType) {
        startAvailabilityBootstrap({ ping: true })
      }
    }

    async function resolveResumableClinicalConsultation(
      customerId: CustomerId,
    ): Promise<string | null> {
      const result = await invokePlatformCoord(
        'resolveResumableClinicalConsultation',
        { customerId },
        () => platformCoordApi.resolveResumableClinicalConsultation({ customerId }),
        { skipInvalidatedHandling: true },
      )
      return result.consultationId
    }

    const customer = ref<ClinicalCustomerData | null>(null)
    const entrySource = ref<ClinicalBookingEntrySource>('account')
    const preferredConsultationId = ref<string | null>(null)
    const sessionConsultType = ref<ConsultationConsultType | null>(null)
    const consultTypeChosen = ref<ConsultationConsultType | null>(null)
    const billingPath = ref<ClinicalBillingPath | null>(null)
    const activeBridgePolicyId = ref<string | null>(null)
    const insuredPayerDisplayName = ref<string | null>(null)
    const insuredPolicySharedAt = ref<Date | null>(null)
    const reservedSubPhase = ref<ClinicalReservedSubPhase>(null)
    const isWaitingForInsuredCoverage = ref(false)
    const insuredCoverageError = ref<string | null>(null)
    type InsuredCoveragePhase =
      'idle' | 'checking_eligibility' | 'ready' | 'full_price_required' | 'failed'
    const insuredCoveragePhase = ref<InsuredCoveragePhase>('idle')
    const insuredCoveragePatientResponsibility = ref<number | null>(null)
    const insuredCoveragePatientResponsibilityBreakdown =
      ref<BridgePatientResponsibilityBreakdown | null>(null)
    const insuredSettlementOutstandingCents = ref<number | null>(null)
    const insuredSettlementPatientResponsibilityBreakdown =
      ref<BridgePatientResponsibilityBreakdown | null>(null)
    const productStep = ref<ClinicalProductStep | null>(null)
    const productPreflowComplete = ref(false)
    const insuranceCategory = ref<ClinicalInsuranceCategory | null>(null)
    const cashPayEligibleReason = ref<ClinicalCashPayEligibleReason | null>(null)
    const hasScheduledConsultation = ref(false)
    const scheduledConsultation = ref<Consultation | null>(null)
    const cancelledAppointmentSummary = ref<ClinicalAccountScheduledSummary | null>(null)
    const reusableBridgePolicyId = ref<string | null>(null)
    const insuranceDetailsEntry = ref<ClinicalInsuranceDetailsEntry | null>(null)
    const chargeFullListPrice = ref(false)
    const insuredPaymentFailureReason = ref<ClinicalInsuredPaymentFailureReason | null>(null)
    const profileDraft = ref<ClinicalProfileDraft>({
      firstName: '',
      lastName: '',
      state: '',
      stateName: '',
      dateOfBirth: '',
      gender: CustomerGender.Unknown,
    })
    const profileDemographicsBaseline = ref<ClinicalCustomerData | null>(null)
    const profileDemographicsUnchanged = ref(false)
    let ensureClinicalVendorsPromise: Promise<unknown> | null = null
    let ensureClinicalVendorsCustomerId: CustomerId | null = null
    type EnsureClinicalVendorsAttemptStatus = 'none' | 'pending' | 'fulfilled' | 'rejected'
    let ensureClinicalVendorsAttemptStatus: EnsureClinicalVendorsAttemptStatus = 'none'

    function startEnsureCustomerClinicalVendors(
      customerId: CustomerId,
      logContext: 'profile' | 'insurance_on_file_confirm',
    ): void {
      ensureClinicalVendorsCustomerId = customerId
      ensureClinicalVendorsAttemptStatus = 'pending'
      const ensurePromise = platformCoordApi.ensureCustomerClinicalVendors({ customerId })
      ensureClinicalVendorsPromise = ensurePromise
      void ensurePromise
        .then(() => {
          if (ensureClinicalVendorsPromise === ensurePromise) {
            ensureClinicalVendorsAttemptStatus = 'fulfilled'
          }
        })
        .catch((err) => {
          if (ensureClinicalVendorsPromise === ensurePromise) {
            ensureClinicalVendorsAttemptStatus = 'rejected'
          }
          const message =
            logContext === 'profile'
              ? 'Failed to start Bridge patient ensure'
              : 'Failed to start Bridge patient ensure on retry'
          logger.warn(message, { customerId, err })
        })
    }

    function captureProfileDemographicsBaseline(source: ClinicalCustomerData) {
      profileDemographicsBaseline.value = { ...source }
      profileDemographicsUnchanged.value = false
    }

    function resetProfileDemographicsTracking() {
      profileDemographicsBaseline.value = null
      profileDemographicsUnchanged.value = false
    }

    const serverConsultation = ref<Consultation | null>(null)
    /** Lifecycle from the latest non-cached Firestore snapshot (or reserve API). */
    const authoritativeServerLifecycleStatus = ref<Consultation['lifecycleStatus'] | null>(null)
    const optimisticConsultationPatch = ref<Partial<Consultation> | null>(null)
    const consultationId = ref<string | null>(null)
    const isSubscribedToConsultation = ref(false)
    let unsubscribeConsultation: (() => void) | null = null
    let unsubscribeBridgeCoverageProjection: (() => void) | null = null
    let unsubscribePayStepStripeCheckoutProjection: (() => void) | null = null
    const payStepStripeCheckoutPaid = ref(false)
    let unsubscribeBridgePolicy: Unsubscribe | null = null
    let unsubscribeBridgePatientProjection: Unsubscribe | null = null
    let unsubscribeLicensedStatesSnapshot: Unsubscribe | null = null

    const licensedStatesCacheLoaded = ref(false)
    const licensedStatesLoadFailed = ref(false)
    const userLicensedStateCodes = ref<Record<string, string[]>>({})
    const userIdToConsultantIdFromCache = ref<Record<string, string>>({})
    const userIdToConsultantDisplayNameFromCache = ref<Record<string, string>>({})
    const supportedStateCodes = computed(() =>
      clinicalBookingSupportedStateCodes(userLicensedStateCodes.value),
    )

    const LICENSED_STATES_LOAD_ERROR_MESSAGE =
      'Unable to load provider licensing. Please try again.'

    function applyLicensedStatesCacheData(data: Record<string, unknown> | undefined) {
      if (!data) {
        userLicensedStateCodes.value = {}
        userIdToConsultantIdFromCache.value = {}
        userIdToConsultantDisplayNameFromCache.value = {}
        licensedStatesCacheLoaded.value = true
        availabilityStore.setLicensedStateMaps({
          userLicensedStateCodes: {},
          userIdToConsultantId: {},
        })
        return
      }
      const parsed = consultantLicensedStatesCacheSchema.safeParse({
        id: CONSULTANT_LICENSED_STATES_CACHE_DOC_ID,
        ...data,
      })
      if (!parsed.success) {
        logger.error('Failed to parse consultant licensed states cache', {
          error: parsed.error,
        })
        userLicensedStateCodes.value = {}
        userIdToConsultantIdFromCache.value = {}
        userIdToConsultantDisplayNameFromCache.value = {}
        licensedStatesCacheLoaded.value = true
        availabilityStore.setLicensedStateMaps({
          userLicensedStateCodes: {},
          userIdToConsultantId: {},
        })
        return
      }
      userLicensedStateCodes.value = parsed.data.userLicensedStateCodes
      userIdToConsultantIdFromCache.value = parsed.data.userIdToConsultantId
      userIdToConsultantDisplayNameFromCache.value = parsed.data.userIdToConsultantDisplayName ?? {}
      licensedStatesCacheLoaded.value = true
      licensedStatesLoadFailed.value = false
      availabilityStore.setLicensedStateMaps({
        userLicensedStateCodes: parsed.data.userLicensedStateCodes,
        userIdToConsultantId: parsed.data.userIdToConsultantId,
      })
    }

    async function subscribeLicensedStates() {
      const cacheRef = doc(firestore, 'consultants_cache', CONSULTANT_LICENSED_STATES_CACHE_DOC_ID)

      try {
        const initialSnap = await getDoc(cacheRef)
        applyLicensedStatesCacheData(
          initialSnap.exists() ? (initialSnap.data() as Record<string, unknown>) : undefined,
        )
      } catch (err) {
        logger.error('Failed to load consultant licensed states cache', { err })
        licensedStatesCacheLoaded.value = false
        licensedStatesLoadFailed.value = true
      }

      if (unsubscribeLicensedStatesSnapshot) {
        return
      }

      unsubscribeLicensedStatesSnapshot = onSnapshot(
        cacheRef,
        (docSnap) => {
          applyLicensedStatesCacheData(
            docSnap.exists() ? (docSnap.data() as Record<string, unknown>) : undefined,
          )
        },
        (err) => {
          logger.error('Error in onSnapshot for consultant licensed states cache', { err })
          if (unsubscribeLicensedStatesSnapshot) {
            unsubscribeLicensedStatesSnapshot()
            unsubscribeLicensedStatesSnapshot = null
          }
          licensedStatesReady = null
          licensedStatesCacheLoaded.value = false
          licensedStatesLoadFailed.value = true
        },
      )
    }

    let licensedStatesReady: Promise<void> | null = null

    /** Loads the licensed-states cache once per subscription; the snapshot keeps it current. */
    function ensureLicensedStatesLoaded(): Promise<void> {
      licensedStatesReady ??= subscribeLicensedStates()
      return licensedStatesReady
    }

    function unsubscribeLicensedStates() {
      licensedStatesReady = null
      if (unsubscribeLicensedStatesSnapshot) {
        unsubscribeLicensedStatesSnapshot()
        unsubscribeLicensedStatesSnapshot = null
      }
      licensedStatesCacheLoaded.value = false
      licensedStatesLoadFailed.value = false
      userLicensedStateCodes.value = {}
      userIdToConsultantIdFromCache.value = {}
      userIdToConsultantDisplayNameFromCache.value = {}
      availabilityStore.setLicensedStateMaps({
        userLicensedStateCodes: {},
        userIdToConsultantId: {},
      })
    }

    function isProfileStateSupported(stateCode: string | null | undefined): boolean {
      if (!licensedStatesCacheLoaded.value) {
        return true
      }
      return isClinicalBookingStateSupported(stateCode, supportedStateCodes.value)
    }

    const userPickedDate = ref<Date | null>(null)
    const userPickedSlot = ref<AvailabilitySlot | null>(null)
    const isDraftSlotReady = ref(false)
    const remoteDraftSelectWriteAcknowledged = ref(false)
    let acknowledgedRemoteDraftSelect: {
      tellescopeUserId: string
      startTimeInMS: number
      durationInMinutes: number
    } | null = null
    const slotSelectionError = ref<string | null>(null)
    const slotSelectionErrorSlotId = ref<string | null>(null)
    const error = ref<string | null>(null)
    /** Slot picker only; the general `error` stays untouched by availability. */
    const availabilityError = ref<string | null>(null)
    const isReserving = ref(false)
    const isMarkingDraftSlot = ref(false)
    let selectSlotGeneration = 0
    let selectSlotDraftWriteChain = Promise.resolve()
    const isConfirming = ref(false)
    const activeReleaseCount = ref(0)
    const isReleasingReservation = computed(() => activeReleaseCount.value > 0)
    const isResettingSession = ref(false)
    const customerCheckoutAcknowledged = ref(false)
    const customerAwaitingCheckoutPropagation = ref(false)
    const customerCheckoutPropagationError = ref<string | null>(null)
    const isConsultationSnapshotLoading = ref(false)
    const isSessionHydrating = ref(false)

    const now = ref(Date.now())
    const clinicalHoldCapReached = ref(false)
    const isExtendingHold = ref(false)
    let countdownTimer: ReturnType<typeof setInterval> | null = null
    let checkoutPropagationTimeoutId: number | null = null
    let operationGeneration = 0
    /** Bumped on every session attach (including same-session re-entry) to invalidate stale reset tokens. */
    let sessionAttachEpoch = 0
    let resetTokenSessionAttachEpoch = 0
    let outstandingLocalDraftSelectAction: 'write' | 'clear' | null = null
    let activeSessionKey: string | null = null
    let releaseHoldInFlight: Promise<void> | null = null
    let releaseHoldInFlightForId: string | null = null
    let resetSessionInFlight: Promise<void> | null = null
    let unsubscribeExistingGateConsultation: Unsubscribe | null = null
    let settleExistingGateLoad: ((consultation: Consultation | null) => void) | null = null

    function stopExistingGateConsultationWatch(): void {
      unsubscribeExistingGateConsultation?.()
      unsubscribeExistingGateConsultation = null
      if (settleExistingGateLoad) {
        const settle = settleExistingGateLoad
        settleExistingGateLoad = null
        settle(scheduledConsultation.value)
      }
    }

    watch(productStep, (step) => {
      if (step !== 'existing_consultation_gate') {
        stopExistingGateConsultationWatch()
      }
      const customerId = customer.value?.id as CustomerId | undefined
      if (step === 'profile' && customerId) {
        prefetchReusableBridgePolicyCoalesced(customerId).catch(() => undefined)
      }
    })

    function resetProductPreflowState() {
      stopExistingGateConsultationWatch()
      const preserveCancelledSummary = productStep.value === 'appointment_cancelled'
      productStep.value = null
      productPreflowComplete.value = false
      insuranceCategory.value = null
      cashPayEligibleReason.value = null
      hasScheduledConsultation.value = false
      scheduledConsultation.value = null
      if (!preserveCancelledSummary) {
        cancelledAppointmentSummary.value = null
      }
      reusableBridgePolicyId.value = null
      insuranceDetailsEntry.value = null
      chargeFullListPrice.value = false
      insuredPaymentFailureReason.value = null
      insuredPayerDisplayName.value = null
      insuredPolicySharedAt.value = null
      resetProfileDemographicsTracking()
      profileDraft.value = {
        firstName: '',
        lastName: '',
        state: '',
        stateName: '',
        dateOfBirth: '',
        gender: CustomerGender.Unknown,
      }
    }

    function persistInsuredDisplaySnapshot() {
      const customerId = customer.value?.id
      if (!customerId) {
        return
      }
      persistClinicalInsuranceDisplay(customerId, {
        insuranceCategory: insuranceCategory.value,
        insuredPayerDisplayName: insuredPayerDisplayName.value,
      })
    }

    function applyPersistedInsuredDisplay(customerId: string) {
      const persisted = restoreClinicalInsuranceDisplay(customerId)
      if (!persisted) {
        return
      }
      if (!insuranceCategory.value && persisted.insuranceCategory) {
        insuranceCategory.value = persisted.insuranceCategory
      }
      if (!insuredPayerDisplayName.value && persisted.insuredPayerDisplayName) {
        insuredPayerDisplayName.value = persisted.insuredPayerDisplayName
      }
    }

    function stateNameFromAbbreviation(abbr: string): string {
      const normalized = abbr.trim().toUpperCase()
      const entry = Object.entries(STATE_ABBREVIATIONS).find(([, code]) => code === normalized)
      return entry?.[0] ?? abbr
    }

    function hydrateProfileDraftFromCustomer(next: ClinicalCustomerData) {
      const stateName = stateNameFromAbbreviation(next.state)
      profileDraft.value = {
        firstName: next.firstName,
        lastName: next.lastName,
        state: next.state,
        stateName,
        dateOfBirth: next.dateOfBirth ?? '',
        gender: next.gender ?? CustomerGender.Unknown,
      }
    }

    function mergeProfileDraftFromCustomer(next: ClinicalCustomerData) {
      const stateName = next.state ? stateNameFromAbbreviation(next.state) : ''
      const incoming = {
        firstName: next.firstName || '',
        lastName: next.lastName || '',
        state: next.state || '',
        stateName,
        dateOfBirth: next.dateOfBirth ?? '',
        gender: next.gender ?? CustomerGender.Unknown,
      }
      const current = profileDraft.value
      profileDraft.value = {
        firstName: current.firstName.trim() || incoming.firstName,
        lastName: current.lastName.trim() || incoming.lastName,
        state: incoming.state.trim() ? incoming.state : current.state.trim(),
        stateName: incoming.stateName.trim() ? incoming.stateName : current.stateName.trim(),
        dateOfBirth: current.dateOfBirth.trim() || incoming.dateOfBirth,
        gender:
          current.gender && current.gender !== CustomerGender.Unknown
            ? current.gender
            : incoming.gender,
      }
    }

    async function loadScheduledConsultationForCustomer(
      customerId: string,
    ): Promise<Consultation | null> {
      const parseGateOccupyingDoc = (docSnap: {
        id: string
        data: () => Record<string, unknown>
      }): Consultation | null => {
        const parsed = consultationSchema.safeParse({
          id: docSnap.id,
          ...consultationSnapshotFieldsToDates(docSnap.data() as Record<string, unknown>),
        })
        if (!parsed.success) {
          logger.warn('Failed to parse scheduled consultation snapshot', {
            consultationId: docSnap.id,
            error: parsed.error,
          })
          return null
        }
        return parsed.data
      }

      const gateQuery = query(
        collection(firestore, 'consultations'),
        where('customerId', '==', customerId),
        where('featureKey', '==', 'clinical_consultation'),
        where('lifecycleStatus', 'in', EXISTING_CONSULTATION_GATE_OCCUPYING_LIFECYCLE_STATUSES),
      )
      const snap = await getDocs(gateQuery)
      if (snap.empty) {
        return null
      }

      const now = new Date()
      const consultations = snap.docs
        .map(parseGateOccupyingDoc)
        .filter((c): c is Consultation => c !== null)
        .filter((c) =>
          isActiveBookingOccupyingClinicalConsultation({
            lifecycleStatus: c.lifecycleStatus,
            operationalStatus: c.operationalStatus,
            scheduledStart: c.scheduledStart,
            createdAt: c.createdAt,
            checkoutExpiresAt: c.checkoutExpiresAt,
            confirmedAt: c.confirmedAt,
            now,
          }),
        )

      return pickGateOccupyingConsultation(consultations)
    }

    async function loadHasScheduledConsultation(
      customerId: string,
      sessionConsultationId?: string | null,
      sessionGen?: number,
    ): Promise<boolean> {
      const scheduled = await loadScheduledConsultationForCustomer(customerId)
      if (sessionGen != null && sessionGen !== operationGeneration) {
        return false
      }
      if (!scheduled) {
        scheduledConsultation.value = null
        return false
      }
      if (
        !isConflictingScheduledConsultation({
          scheduledConsultationId: scheduled.id,
          sessionConsultationId,
        })
      ) {
        scheduledConsultation.value = null
        return false
      }
      scheduledConsultation.value = scheduled
      return true
    }

    const followupBookingEligible = ref(false)
    const preferredFollowupTellescopeUserId = ref<string | null>(null)
    const followupInsurancePolicyLocked = ref(false)
    let followupBookingContextLoaded = false
    const HUB_FOLLOWUP_STAGE_MAX_AGE_MS = 60_000

    let pendingHubFollowupBooking: {
      customerId: CustomerId
      stagedAt: number
      context: {
        eligible: boolean
        preferredTellescopeUserId: string | null
        insurancePolicyLocked?: boolean
      }
    } | null = null

    function applyFollowupBookingContext(context: {
      eligible: boolean
      preferredTellescopeUserId: string | null
      insurancePolicyLocked?: boolean
    }) {
      followupBookingEligible.value = context.eligible
      preferredFollowupTellescopeUserId.value = context.preferredTellescopeUserId
      followupInsurancePolicyLocked.value = context.insurancePolicyLocked ?? false
      followupBookingContextLoaded = true
    }

    function stageFollowupBookingFromHub(
      context: {
        eligible: boolean
        preferredTellescopeUserId: string | null
        insurancePolicyLocked?: boolean
      },
      customerId?: CustomerId,
    ) {
      const id = (customerId ?? customer.value?.id) as CustomerId | undefined
      if (!id) {
        return
      }
      pendingHubFollowupBooking = {
        customerId: id,
        stagedAt: Date.now(),
        context,
      }
    }

    function consumePendingHubFollowupStage(customerId: CustomerId): {
      eligible: boolean
      preferredTellescopeUserId: string | null
      insurancePolicyLocked?: boolean
    } | null {
      const stage = pendingHubFollowupBooking
      if (!stage) {
        return null
      }
      if (
        stage.customerId !== customerId ||
        Date.now() - stage.stagedAt > HUB_FOLLOWUP_STAGE_MAX_AGE_MS
      ) {
        pendingHubFollowupBooking = null
        return null
      }
      pendingHubFollowupBooking = null
      return stage.context
    }

    function syncFollowupBookingFromHub() {
      const hubStore = useCustomerConsultationsHubStore()
      if (followupBookingContextLoaded) {
        return
      }
      if (!hubStore.hasLoaded) {
        return
      }
      applyFollowupBookingContext({
        eligible: hubStore.followupBooking.eligible,
        preferredTellescopeUserId: hubStore.followupBooking.preferredTellescopeUserId,
        insurancePolicyLocked: hubStore.followupBooking.insurancePolicyLocked,
      })
    }

    async function ensureFollowupBookingContext(gen: number) {
      try {
        const customerId = customer.value?.id as CustomerId | undefined
        if (!customerId) {
          return
        }
        const staged = consumePendingHubFollowupStage(customerId)
        if (staged) {
          applyFollowupBookingContext(staged)
          return
        }
        syncFollowupBookingFromHub()
        if (followupBookingContextLoaded) {
          return
        }
        const context = await prefetchFollowupBookingContextCoalesced(customerId)
        if (gen !== operationGeneration) {
          return
        }
        applyFollowupBookingContext(context)
      } catch (err) {
        logger.error('Failed to ensure followup booking context; falling back to ineligible', {
          err,
        })
        if (gen !== operationGeneration) {
          return
        }
        applyFollowupBookingContext({
          eligible: false,
          preferredTellescopeUserId: null,
        })
      }
    }

    /** Settles once follow-up eligibility for the current session is known (never rejects). */
    let followupContextReady: Promise<void> = Promise.resolve()

    function resolveAutoConsultType(): ConsultationConsultType {
      return followupBookingEligible.value ? 'clinical_followup' : 'clinical_main'
    }

    async function resolveSessionAutoConsultType(): Promise<ConsultationConsultType> {
      await followupContextReady
      return resolveAutoConsultType()
    }

    type InsuredDraftCreate = {
      generation: number
      promise: Promise<void>
      failed: boolean
    }
    let insuredDraftCreate: InsuredDraftCreate | null = null

    async function completeIntro() {
      if (hasScheduledConsultation.value) {
        productStep.value = 'existing_consultation_gate'
        return
      }
      productStep.value = 'profile'
    }

    function handleUnsupportedState() {
      productStep.value = 'unsupported_state'
      productPreflowComplete.value = false
    }

    function syncProfileDraftStateFromConfirm(args: { stateName: string; state?: string | null }) {
      profileDraft.value = {
        ...profileDraft.value,
        stateName: args.stateName,
        state: args.state ?? '',
      }
    }

    async function completeProfile(next: ClinicalProfileDraft & { fullName: string }) {
      if (!customer.value) {
        throw new Error('Customer is required')
      }
      const gen = operationGeneration
      const baseline = profileDemographicsBaseline.value ?? { ...customer.value }
      profileDraft.value = {
        firstName: next.firstName,
        lastName: next.lastName,
        state: next.state,
        stateName: next.stateName,
        dateOfBirth: next.dateOfBirth,
        gender: next.gender,
      }
      customer.value = {
        ...customer.value,
        firstName: next.firstName,
        lastName: next.lastName,
        state: next.state,
        fullName: next.fullName,
        dateOfBirth: next.dateOfBirth,
        gender: next.gender,
      }
      await ensureLicensedStatesLoaded()
      if (gen !== operationGeneration || !customer.value) {
        return
      }
      if (!isProfileStateSupported(next.state)) {
        handleUnsupportedState()
        return
      }
      profileDemographicsUnchanged.value = customerBridgeProfileDemographicsEqual(baseline, {
        ...customer.value,
        firstName: next.firstName,
        lastName: next.lastName,
        dateOfBirth: next.dateOfBirth,
        state: next.state,
        stateOfResidence: next.stateName,
      })
      const customerId = customer.value.id as CustomerId
      // Start Bridge patient sync without blocking the next step. Attach waits for it.
      startEnsureCustomerClinicalVendors(customerId, 'profile')
      if (profileDemographicsUnchanged.value) {
        const routedToInsuranceOnFile = await tryRouteToInsuranceOnFileAfterProfile(customerId, gen)
        if (gen !== operationGeneration || customer.value?.id !== customerId) {
          return
        }
        if (routedToInsuranceOnFile) {
          return
        }
      }
      productStep.value = 'insurance_category'
    }

    async function tryRouteToInsuranceOnFileAfterProfile(
      customerId: CustomerId,
      gen: number,
    ): Promise<boolean> {
      reusableBridgePolicyId.value = null
      insuredPayerDisplayName.value = null
      insuredPolicySharedAt.value = null
      try {
        const { policyId: reusablePolicyId, onFileDisplay } =
          await prefetchReusableBridgePolicyCoalesced(customerId)
        if (gen !== operationGeneration || customer.value?.id !== customerId) {
          return false
        }
        if (!reusablePolicyId) {
          return false
        }
        reusableBridgePolicyId.value = reusablePolicyId
        if (onFileDisplay?.payerDisplayName?.trim()) {
          insuredPayerDisplayName.value = onFileDisplay.payerDisplayName.trim()
        }
        insuredPolicySharedAt.value = onFileDisplay?.sharedAt ?? null
        productStep.value = 'insurance_on_file'
        return true
      } catch (err) {
        logger.warn('Failed to read reusable policy from projection', {
          customerId,
          err,
        })
        return false
      }
    }

    async function selectInsuranceCategory(category: ClinicalInsuranceCategory) {
      insuranceCategory.value = category
      persistInsuredDisplaySnapshot()
      const billing = insuranceCategoryBillingPath(category)

      if (billing === 'self_pay') {
        cashPayEligibleReason.value = resolveCashPayEligibleReason(category)
        productStep.value = 'cash_pay_eligible'
        return
      }

      // Bridge patient create finishes in the background after profile ensure.
      // Verify calls ensureBridgePatient; do not block this step on the projection.
      productStep.value = 'insurance_details'
    }

    async function openInsuranceDetailsAfterOnFileConfirmFailure(
      policyId: BridgePolicyId,
    ): Promise<void> {
      const display = await fetchBridgePolicyOnFileDisplay(policyId)
      insuranceDetailsEntry.value = {
        errorMessage: INSURANCE_ON_FILE_CONFIRM_FAILED_MESSAGE,
        payerId: display.payerId,
        payerDisplayName: display.payerDisplayName,
        memberId: display.memberId,
      }
      reusableBridgePolicyId.value = null
      productStep.value = 'insurance_details'
    }

    function clearInsuranceDetailsEntry(): void {
      insuranceDetailsEntry.value = null
    }

    async function confirmInsuranceOnFile(): Promise<void> {
      const policyId = reusableBridgePolicyId.value as BridgePolicyId | null
      const customerId = customer.value?.id as CustomerId | undefined
      if (!policyId || !customerId) {
        throw new Error('Insurance on file is not available')
      }

      const needsFreshEnsure =
        ensureClinicalVendorsCustomerId !== customerId ||
        ensureClinicalVendorsPromise === null ||
        ensureClinicalVendorsAttemptStatus === 'rejected'
      if (needsFreshEnsure) {
        startEnsureCustomerClinicalVendors(customerId, 'insurance_on_file_confirm')
      }

      const promiseToAwait = ensureClinicalVendorsPromise
      try {
        await promiseToAwait
      } catch (err) {
        if (ensureClinicalVendorsPromise === promiseToAwait) {
          ensureClinicalVendorsPromise = null
          ensureClinicalVendorsCustomerId = null
          ensureClinicalVendorsAttemptStatus = 'none'
        }
        throw err
      }

      await waitForClinicalVendorPatientsReady(customerId, {
        keys: ['bridgePatient'],
      })

      // The wait above resolves on a listener snapshot (possibly cached) and returns nothing, so
      // consent freshness needs its own server read.
      const projection = await fetchBridgePatientProjection(customerId)
      const consentFresh = isBridgeConsentFreshForMaximumConsultationDate(
        projection?.consent?.consentedAt,
        clinicalBookingTimezone.value,
      )
      if (!consentFresh) {
        logger.warn('Bridge consent is stale for insurance on file confirm', {
          customerId,
          policyId,
          consentedAt: projection?.consent?.consentedAt ?? null,
        })
        await openInsuranceDetailsAfterOnFileConfirmFailure(policyId)
        throw new Error(INSURANCE_ON_FILE_CONFIRM_FAILED_MESSAGE)
      }

      const projectionPolicyId = await readReusableBridgePolicyFromProjection(customerId)
      if (projectionPolicyId !== policyId) {
        invalidateReusableBridgePolicyPrefetch(customerId)
        reusableBridgePolicyId.value = null
        insuredPayerDisplayName.value = null
        insuredPolicySharedAt.value = null
        productStep.value = 'insurance_category'
        toastStore.error('Your insurance details changed. Please confirm them again.')
        throw new Error('Your insurance details changed. Please confirm them again.')
      }

      try {
        // Display-only read of the policy doc; independent of the capture/ensure/attach chain.
        const payerDisplayNameRead = Promise.resolve(
          insuredPayerDisplayName.value ?? fetchBridgePolicyPayerDisplayName(policyId),
        )
        payerDisplayNameRead.catch(() => undefined)
        await tryReuseExistingBridgePolicy({
          customerId,
          policyId,
        })
        const payerDisplayName = await payerDisplayNameRead
        await finishInsuredInsuranceSetup(
          policyId,
          customer.value?.state ?? null,
          payerDisplayName,
          {
            showInsuranceVerified: false,
          },
        )
        invalidateReusableBridgePolicyPrefetch(customerId)
      } catch (err) {
        logger.warn('Failed to reuse existing Bridge policy from insurance on file', {
          customerId,
          policyId,
          err,
        })
        await openInsuranceDetailsAfterOnFileConfirmFailure(policyId)
        throw err
      }
    }

    function declineInsuranceOnFile() {
      const customerId = customer.value?.id as CustomerId | undefined
      if (customerId) {
        invalidateReusableBridgePolicyPrefetch(customerId)
      }
      reusableBridgePolicyId.value = null
      insuredPayerDisplayName.value = null
      insuredPolicySharedAt.value = null
      insuranceDetailsEntry.value = null
      productStep.value = 'insurance_category'
    }

    async function ensureInsuredDraftForVerifiedStep(): Promise<void> {
      const pending = insuredDraftCreate
      if (pending?.generation === operationGeneration) {
        try {
          await pending.promise
        } catch (err) {
          if (insuredDraftCreate === pending) {
            insuredDraftCreate = null
          }
          // A blocked create runs resolve/resume through the normal path below, from this click.
          // Any other failure is surfaced once; the next Continue creates again.
          if (!isClinicalCreateBlockedError(err)) {
            throw err
          }
        }
        if (insuredDraftCreate === pending) {
          insuredDraftCreate = null
        }
      }
      if (consultationId.value == null && consultation.value?.id == null) {
        const consultType = consultTypeChosen.value ?? (await resolveSessionAutoConsultType())
        await createClinicalConsultation('insured', consultType, {
          bridgeEligibilityState: resolveInsuredVisitStateForBridge(),
        })
        await setConsultType(consultType)
      }
    }

    /** The footer callback has no catch, so failures are reported here instead of thrown. */
    async function completeInsuranceVerifiedStep(): Promise<void> {
      try {
        await ensureInsuredDraftForVerifiedStep()
      } catch (err) {
        logger.error('Failed to create insured draft from the insurance verified step', { err })
        if (!error.value) {
          toastUnlessClinicalBookingInvalidated(
            err,
            'Failed to start your booking. Please try again.',
          )
        }
        return
      }
      productPreflowComplete.value = true
      productStep.value = null
      startAvailabilityBootstrap()
    }

    async function cancelScheduledConsultation(): Promise<void> {
      const scheduled = scheduledConsultation.value
      if (!scheduled) {
        throw new Error('No scheduled consultation to cancel')
      }

      cancelledAppointmentSummary.value = formatClinicalAccountScheduledSummary(scheduled)

      await invokePlatformCoord(
        'cancelClinicalConsultation',
        {
          consultationId: scheduled.id,
          reason: 'customer_aborted',
        },
        () =>
          platformCoordApi.cancelClinicalConsultation({
            consultationId: scheduled.id as ConsultationId,
            reason: 'customer_aborted',
          }),
      )

      scheduledConsultation.value = null
      hasScheduledConsultation.value = false
      productPreflowComplete.value = false
      productStep.value = 'appointment_cancelled'
    }

    function bookAgainAfterCancellation() {
      cancelledAppointmentSummary.value = null
      reusableBridgePolicyId.value = null
      productPreflowComplete.value = false
      productStep.value = 'intro'
      void ensureIntroEmptyDraftConsultation(operationGeneration)
    }

    async function ensureIntroEmptyDraftConsultation(sessionGen: number) {
      if (!customer.value?.id) {
        return
      }
      const consultType =
        sessionConsultType.value ?? consultTypeChosen.value ?? ('clinical_main' as const)
      const createArgs = {
        customerId: customer.value.id as CustomerId,
        consultType,
        ...(entrySource.value === 'signup' ? { signupBooking: true as const } : {}),
      }
      try {
        const result = await invokePlatformCoord('createClinicalConsultation', createArgs, () =>
          platformCoordApi.createClinicalConsultation(createArgs),
        )
        if (sessionGen !== operationGeneration) {
          return
        }
        const now = new Date()
        optimisticConsultationPatch.value = {
          id: result.consultationId as ConsultationId,
          customerId: customer.value.id as CustomerId,
          featureKey: 'clinical_consultation',
          consultType,
          lifecycleStatus: result.lifecycleStatus,
          operationalStatus: 'draft',
          createdAt: consultation.value?.createdAt ?? now,
          updatedAt: now,
        }
        subscribeConsultation(result.consultationId)
        authoritativeServerLifecycleStatus.value = result.lifecycleStatus
      } catch (err) {
        logger.warn('Intro empty clinical consultation draft failed', { err })
      }
    }

    async function confirmCashPayEligible(): Promise<void> {
      const consultType = await resolveSessionAutoConsultType()
      await createClinicalConsultation('self_pay', consultType)
      productPreflowComplete.value = true
      productStep.value = null
      cashPayEligibleReason.value = null
      await setConsultType(consultType)
    }

    async function beginCustomerProductPreflow() {
      if (preferredConsultationId.value) {
        productPreflowComplete.value = true
        productStep.value = null
        return
      }
      productPreflowComplete.value = false
      productStep.value = hasScheduledConsultation.value ? 'existing_consultation_gate' : 'intro'
      if (productStep.value === 'intro') {
        void ensureIntroEmptyDraftConsultation(operationGeneration)
      }
    }

    function applyExistingGateConsultationSnapshot(
      consultationId: string,
      data: Record<string, unknown> | undefined,
      exists: boolean,
    ): Consultation | null {
      if (!exists || !data) {
        scheduledConsultation.value = null
        return null
      }
      const parsed = consultationSchema.safeParse({
        id: consultationId,
        ...consultationSnapshotFieldsToDates(data),
      })
      if (!parsed.success) {
        logger.warn('Failed to parse existing-consultation gate snapshot', {
          consultationId,
          error: parsed.error,
        })
        scheduledConsultation.value = null
        return null
      }
      scheduledConsultation.value = parsed.data
      return parsed.data
    }

    async function loadConsultationForExistingGate(
      consultationId: string,
    ): Promise<Consultation | null> {
      stopExistingGateConsultationWatch()
      return new Promise((resolve) => {
        let settled = false
        const finish = (consultation: Consultation | null) => {
          if (settled) {
            return
          }
          settled = true
          if (settleExistingGateLoad === finish) {
            settleExistingGateLoad = null
          }
          resolve(consultation)
        }
        settleExistingGateLoad = finish
        unsubscribeExistingGateConsultation = onSnapshot(
          doc(firestore, 'consultations', consultationId),
          (snap) => {
            finish(
              applyExistingGateConsultationSnapshot(
                consultationId,
                snap.exists() ? (snap.data() as Record<string, unknown>) : undefined,
                snap.exists(),
              ),
            )
          },
          (err) => {
            logger.warn('Existing consultation gate listener failed', {
              consultationId,
              err,
            })
            scheduledConsultation.value = null
            finish(null)
          },
        )
      })
    }

    async function prepareExistingConsultationGateView(
      consultationId?: string | null,
    ): Promise<void> {
      productPreflowComplete.value = false
      productStep.value = 'existing_consultation_gate'
      if (consultationId) {
        await loadConsultationForExistingGate(consultationId)
      }
    }

    function mapCoverageRejectFromError(
      err: BridgeCoverageFailureError,
    ): ClinicalInsuredPaymentFailureReason {
      switch (err.status) {
        case 'INELIGIBLE':
          return 'ineligible'
        case 'UNINSURED':
          return 'uninsured'
        case 'INEFFECTIVE':
          return 'ineffective'
        default:
          return 'unknown'
      }
    }

    async function confirmAfterCoverageOutcome(): Promise<void> {
      if (insuredCoveragePhase.value === 'ready') {
        await confirmBooking()
        return
      }
      if (insuredCoveragePhase.value === 'full_price_required') {
        const ok = await confirmBooking({ billingPath: 'self_pay' })
        if (ok) {
          chargeFullListPrice.value = true
          setBillingPath('self_pay')
        }
      }
    }

    async function runHardVerificationAndContinue(): Promise<void> {
      if (!isInsuredPath.value || currentStep.value !== 'reserved') {
        return
      }
      if (
        insuredCoveragePhase.value === 'ready' ||
        insuredCoveragePhase.value === 'full_price_required'
      ) {
        await confirmAfterCoverageOutcome()
        return
      }
      if (isWaitingForInsuredCoverage.value || insuredCoveragePhase.value === 'failed') {
        return
      }

      const success = await waitForInsuredCoverageProjection()
      if (success) {
        await confirmAfterCoverageOutcome()
      }
    }

    function setBillingPath(path: ClinicalBillingPath) {
      billingPath.value = path
    }

    function normalizeInsuredVisitState(state: string | undefined | null): string | null {
      const code = toUsStateCode(state?.trim())
      const normalized = code ?? state?.trim().toUpperCase()
      return normalized && normalized.length === 2 ? normalized : null
    }

    function resolveInsuredVisitStateForBridge(): string | null {
      return (
        normalizeInsuredVisitState(consultation.value?.bridgeEligibilityState) ??
        normalizeInsuredVisitState(customer.value?.state)
      )
    }

    function completeInsuranceVerify(policyId: string | null) {
      activeBridgePolicyId.value = policyId
      billingPath.value = 'insured'
    }

    async function createClinicalConsultation(
      nextBillingPath: ClinicalBillingPath,
      next: ConsultationConsultType,
      options?: {
        bridgeEligibilityState?: string | null
        /** Background create: write nothing once the session that started it is gone. */
        sessionGeneration?: number
        /** Rethrow a create-blocked error so the caller can run resolve/resume at a safe moment. */
        deferBlockedResume?: boolean
      },
    ): Promise<void> {
      if (!customer.value) {
        throw new Error('Customer is required to create clinical consultation')
      }
      const isStaleSession = () =>
        options?.sessionGeneration != null && options.sessionGeneration !== operationGeneration
      if (isStaleSession()) {
        return
      }
      billingPath.value = nextBillingPath
      userPickedDate.value = null
      await selectSlot(null)
      if (isStaleSession()) {
        return
      }

      const bridgeEligibilityState =
        nextBillingPath === 'insured'
          ? (normalizeInsuredVisitState(options?.bridgeEligibilityState ?? null) ??
            resolveInsuredVisitStateForBridge())
          : null
      const createArgs = {
        customerId: customer.value.id as CustomerId,
        billingPath: nextBillingPath,
        consultType: next,
        ...(bridgeEligibilityState ? { bridgeEligibilityState } : {}),
      }
      try {
        const result = await invokePlatformCoord('createClinicalConsultation', createArgs, () =>
          platformCoordApi.createClinicalConsultation(createArgs),
        )
        if (isStaleSession()) {
          return
        }

        consultTypeChosen.value = next
        const now = new Date()
        optimisticConsultationPatch.value = {
          id: result.consultationId as ConsultationId,
          customerId: customer.value.id as CustomerId,
          featureKey: 'clinical_consultation',
          consultType: next,
          lifecycleStatus: result.lifecycleStatus,
          operationalStatus: 'draft',
          billingPath: nextBillingPath,
          ...(bridgeEligibilityState ? { bridgeEligibilityState } : {}),
          createdAt: consultation.value?.createdAt ?? now,
          updatedAt: now,
        }
        subscribeConsultation(result.consultationId)
        authoritativeServerLifecycleStatus.value = result.lifecycleStatus
      } catch (err) {
        if (isStaleSession()) {
          return
        }
        if (isClinicalCreateBlockedError(err) && !options?.deferBlockedResume) {
          const resolvedId = await resolveResumableClinicalConsultation(
            customer.value.id as CustomerId,
          )
          if (!resolvedId) {
            reportClinicalResumeHardError(err)
            error.value = 'Unable to resume your booking. Please try again or contact support.'
            throw err
          }
          await resumeConsultationAfterResolve(resolvedId, next, {
            rejectSelfPayBillingPath: nextBillingPath === 'insured',
          })
          return
        }
        throw err
      }
    }

    async function finishInsuredInsuranceSetup(
      policyId: string | null,
      visitState?: string | null,
      payerDisplayName?: string | null,
      options?: { showInsuranceVerified?: boolean },
    ): Promise<void> {
      completeInsuranceVerify(policyId)
      const normalizedVisitState = normalizeInsuredVisitState(visitState ?? null)
      const trimmedPayerName = payerDisplayName?.trim()
      if (trimmedPayerName) {
        insuredPayerDisplayName.value = trimmedPayerName
      }
      persistInsuredDisplaySnapshot()

      if (options?.showInsuranceVerified) {
        productPreflowComplete.value = false
        productStep.value = 'insurance_verified'
        startInsuredDraftCreate(normalizedVisitState)
        return
      }

      await createInsuredDraftAndSetConsultType(normalizedVisitState)
      productPreflowComplete.value = true
      productStep.value = null
      const customerId = customer.value?.id as CustomerId | undefined
      if (customerId) {
        invalidateReusableBridgePolicyPrefetch(customerId)
      }
    }

    async function createInsuredDraftAndSetConsultType(
      normalizedVisitState: string | null,
      sessionGeneration?: number,
    ): Promise<void> {
      const isStaleSession = () =>
        sessionGeneration != null && sessionGeneration !== operationGeneration
      const consultType = await resolveSessionAutoConsultType()
      if (isStaleSession()) {
        return
      }
      const peState = normalizedVisitState ?? resolveInsuredVisitStateForBridge()
      if (peState) {
        prefetchBookableProviderEligibility({
          state: peState,
          consultType,
          customerId: customer.value?.id as CustomerId,
          policyId: activeBridgePolicyId.value,
        })
      }
      await createClinicalConsultation('insured', consultType, {
        bridgeEligibilityState: normalizedVisitState,
        ...(sessionGeneration != null ? { sessionGeneration, deferBlockedResume: true } : {}),
      })
      if (isStaleSession()) {
        return
      }
      await setConsultType(consultType)
    }

    /**
     * The verified page is static, so the draft is created while the user reads it;
     * `completeInsuranceVerifiedStep` awaits this promise instead of creating again.
     */
    function startInsuredDraftCreate(normalizedVisitState: string | null): void {
      const generation = operationGeneration
      const current = insuredDraftCreate
      if (current?.generation === generation && !current.failed) {
        return
      }
      const entry: InsuredDraftCreate = {
        generation,
        failed: false,
        promise: createInsuredDraftAndSetConsultType(normalizedVisitState, generation),
      }
      entry.promise.catch((err) => {
        entry.failed = true
        logger.warn('Background insured draft create failed', { err })
      })
      insuredDraftCreate = entry
    }

    async function completeClinicalInsuranceVerifyAsSelfPay(): Promise<void> {
      setBillingPath('self_pay')
      const consultType = await resolveSessionAutoConsultType()
      await createClinicalConsultation('self_pay', consultType)
      productPreflowComplete.value = true
      productStep.value = null
      await setConsultType(consultType)
    }

    /**
     * Poll `projections/bridgeCoverage` until ELIGIBLE or terminal reject.
     * Bridge SE create/poll is started server-side on reserve — no FE Bridge callables.
     */
    async function waitForInsuredCoverageProjection(): Promise<boolean> {
      if (!isInsuredPath.value || currentStep.value !== 'reserved') {
        return false
      }
      if (isWaitingForInsuredCoverage.value) {
        return (
          insuredCoveragePhase.value === 'ready' ||
          insuredCoveragePhase.value === 'full_price_required'
        )
      }
      if (
        insuredCoveragePhase.value === 'ready' ||
        insuredCoveragePhase.value === 'full_price_required'
      ) {
        return true
      }

      const id = consultationId.value
      if (!id) {
        insuredCoverageError.value = 'Missing consultation for coverage verification.'
        insuredCoveragePhase.value = 'failed'
        return false
      }

      const reservedSlotKey = consultation.value?.reservedSlotKey
      if (!reservedSlotKey) {
        logger.error('Missing reservedSlotKey for insured bridgeCoverage projection wait', {
          consultationId: id,
          currentStep: currentStep.value,
          lifecycleStatus: consultation.value?.lifecycleStatus,
        })
        insuredCoverageError.value = 'Missing reservation details for coverage verification.'
        insuredCoveragePhase.value = 'failed'
        return false
      }

      isWaitingForInsuredCoverage.value = true
      insuredCoverageError.value = null
      insuredCoveragePhase.value = 'checking_eligibility'

      logger.info('Starting insured bridgeCoverage projection wait', {
        consultationId: id,
        reservedSlotKey,
        currentStep: currentStep.value,
        lifecycleStatus: consultation.value?.lifecycleStatus,
      })

      try {
        const projection = await waitForBridgeCoverageReady({
          consultationId: id as ConsultationId,
          reservedSlotKey,
        })
        insuredCoveragePatientResponsibility.value = projection.patientResponsibility!
        insuredCoveragePatientResponsibilityBreakdown.value =
          projection.patientResponsibilityBreakdown ?? null
        logger.info('Insured bridgeCoverage projection wait succeeded', {
          consultationId: id,
          reservedSlotKey,
          patientResponsibility: projection.patientResponsibility,
        })
        insuredCoveragePhase.value = 'ready'
        return true
      } catch (err: unknown) {
        if (err instanceof BridgeCoverageWaitTimeoutError) {
          logger.error('Bridge coverage projection wait timed out', {
            consultationId: id,
            reservedSlotKey,
            err,
          })
          chargeFullListPrice.value = true
          insuredPaymentFailureReason.value = 'policy_invalid'
          insuredCoveragePhase.value = 'full_price_required'
          return true
        }

        if (err instanceof BridgeCoverageFailureError) {
          logger.info('Insured bridgeCoverage projection terminal reject', {
            consultationId: id,
            reservedSlotKey,
            status: err.status,
          })
          chargeFullListPrice.value = true
          insuredPaymentFailureReason.value = mapCoverageRejectFromError(err)
          insuredCoveragePhase.value = 'full_price_required'
          return true
        }

        const message = err instanceof Error ? err.message : String(err)
        logger.error('Bridge coverage projection wait failed', {
          consultationId: id,
          reservedSlotKey,
          err,
        })
        insuredCoverageError.value = message
        insuredCoveragePhase.value = 'failed'
        return false
      } finally {
        isWaitingForInsuredCoverage.value = false
      }
    }

    async function switchReservedToSelfPay(): Promise<void> {
      const ok = await confirmBooking({ billingPath: 'self_pay' })
      if (ok) {
        chargeFullListPrice.value = true
        setBillingPath('self_pay')
      }
    }

    function resetInsuredCoverageState() {
      insuredCoverageError.value = null
      insuredCoveragePhase.value = 'idle'
      insuredCoveragePatientResponsibility.value = null
      insuredCoveragePatientResponsibilityBreakdown.value = null
      insuredSettlementOutstandingCents.value = null
      insuredSettlementPatientResponsibilityBreakdown.value = null
      isWaitingForInsuredCoverage.value = false
    }

    function applyBridgeCoverageCopayHydration(projection: BridgeCoverageProjection) {
      if (!isInsuredPath.value || chargeFullListPrice.value) {
        return
      }
      if (projection.status === 'ELIGIBLE' && projection.patientResponsibility != null) {
        insuredCoveragePatientResponsibility.value = projection.patientResponsibility
        insuredCoveragePatientResponsibilityBreakdown.value =
          projection.patientResponsibilityBreakdown ?? null
      }
    }

    function applyBridgeCoverageSettlementHydration(projection: BridgeCoverageProjection) {
      if (!isInsuredPath.value || chargeFullListPrice.value) {
        return
      }
      insuredSettlementOutstandingCents.value = projection.settlementRemainingCents ?? null
      insuredSettlementPatientResponsibilityBreakdown.value =
        projection.finalPatientResponsibilityBreakdown ?? null
    }

    function applyBridgeCoverageProjectionHydration(projection: BridgeCoverageProjection) {
      if (currentStep.value === 'pending_settlement_payment') {
        applyBridgeCoverageSettlementHydration(projection)
        return
      }
      applyBridgeCoverageCopayHydration(projection)
    }

    function unsubscribeBridgeCoverageProjectionSnapshot() {
      if (unsubscribeBridgeCoverageProjection) {
        unsubscribeBridgeCoverageProjection()
        unsubscribeBridgeCoverageProjection = null
      }
    }

    function resolvePayStepStripeCheckoutProjectionKey(step: ClinicalBookingStep): string | null {
      if (step === 'payment_pending') {
        return STRIPE_CHECKOUT_PROJECTION_KEY
      }
      if (step === 'pending_settlement_payment') {
        return STRIPE_SETTLEMENT_PAYMENT_CHECKOUT_PROJECTION_KEY
      }
      return null
    }

    function unsubscribePayStepStripeCheckoutProjectionSnapshot() {
      if (unsubscribePayStepStripeCheckoutProjection) {
        unsubscribePayStepStripeCheckoutProjection()
        unsubscribePayStepStripeCheckoutProjection = null
      }
      payStepStripeCheckoutPaid.value = false
    }

    function subscribePayStepStripeCheckoutProjection(id: string, projectionKey: string) {
      unsubscribePayStepStripeCheckoutProjectionSnapshot()
      const refDoc = doc(firestore, 'consultations', id, 'projections', projectionKey)
      unsubscribePayStepStripeCheckoutProjection = onSnapshot(
        refDoc,
        (snap) => {
          if (!snap.exists()) {
            payStepStripeCheckoutPaid.value = false
            return
          }
          const parsed = stripeCheckoutProjectionSchema.safeParse({
            id: snap.id,
            ...snap.data(),
          })
          if (!parsed.success) {
            logger.error('Failed to parse pay-step stripe checkout projection', {
              consultationId: id,
              projectionKey,
              error: parsed.error,
            })
            payStepStripeCheckoutPaid.value = false
            return
          }
          payStepStripeCheckoutPaid.value = isStripeCheckoutProjectionPaid(parsed.data)
        },
        (err) => {
          logger.error('Pay-step stripe checkout projection snapshot error', {
            consultationId: id,
            projectionKey,
            err,
          })
        },
      )
    }

    function unsubscribeBridgePolicySnapshot() {
      if (unsubscribeBridgePolicy) {
        unsubscribeBridgePolicy()
        unsubscribeBridgePolicy = null
      }
    }

    function unsubscribeBridgePatientProjectionSnapshot() {
      if (unsubscribeBridgePatientProjection) {
        unsubscribeBridgePatientProjection()
        unsubscribeBridgePatientProjection = null
      }
    }

    function hydratePayerFromBridgePolicy(policyId: string) {
      unsubscribeBridgePolicySnapshot()
      unsubscribeBridgePolicy = subscribeBridgePolicy(
        policyId as BridgePolicyId,
        ({ policy, error }) => {
          if (error) {
            logger.error('Bridge policy snapshot error for payer hydration', { policyId, error })
            return
          }
          if (!policy || insuredPayerDisplayName.value) {
            return
          }
          const displayName = bridgePolicyPayerDisplayName(policy)
          if (displayName) {
            insuredPayerDisplayName.value = displayName
            persistInsuredDisplaySnapshot()
          }
        },
      )
    }

    function subscribeBridgePatientForPolicyHydration(customerId: CustomerId) {
      unsubscribeBridgePatientProjectionSnapshot()
      const refDoc = doc(firestore, 'customers', customerId, 'projections', 'bridgePatient')
      unsubscribeBridgePatientProjection = onSnapshot(
        refDoc,
        (snap) => {
          if (!snap.exists()) {
            return
          }
          const parsed = bridgePatientProjectionSchema.safeParse({
            id: snap.id,
            ...snap.data(),
          })
          if (!parsed.success || !parsed.data.policyId) {
            return
          }
          if (activeBridgePolicyId.value !== parsed.data.policyId) {
            activeBridgePolicyId.value = parsed.data.policyId
            unsubscribeBridgePolicySnapshot()
          }
          if (!insuredPayerDisplayName.value && !unsubscribeBridgePolicy) {
            hydratePayerFromBridgePolicy(parsed.data.policyId)
          }
        },
        (err) => {
          logger.error('Bridge patient projection snapshot error for payer hydration', {
            customerId,
            err,
          })
        },
      )
    }

    function shouldSubscribeBridgeCoverageForCopay(step: ClinicalBookingStep): boolean {
      return (
        step === 'payment_pending' ||
        step === 'provisioning' ||
        step === 'scheduled' ||
        step === 'pending_settlement_payment'
      )
    }

    function subscribeBridgeCoverageForPaymentPending(id: string) {
      unsubscribeBridgeCoverageProjectionSnapshot()
      const refDoc = doc(firestore, 'consultations', id, 'projections', 'bridgeCoverage')
      unsubscribeBridgeCoverageProjection = onSnapshot(
        refDoc,
        (snap) => {
          if (!snap.exists()) {
            return
          }
          const parsed = bridgeCoverageProjectionSchema.safeParse({
            id: snap.id,
            ...snap.data(),
          })
          if (!parsed.success) {
            logger.error('Failed to parse bridgeCoverage projection for copay hydration', {
              consultationId: id,
              error: parsed.error,
            })
            return
          }
          applyBridgeCoverageProjectionHydration(parsed.data)
        },
        (err) => {
          logger.error('Bridge coverage projection snapshot error for copay hydration', {
            consultationId: id,
            err,
          })
        },
      )
    }

    function buildSessionKey(args: StartClinicalBookingSessionArgs): string {
      return [
        args.customer.id,
        args.consultationId ?? '',
        args.consultType ?? '',
        args.entrySource ?? 'account',
      ].join('|')
    }

    const consultation = computed(() =>
      mergeConsultation(serverConsultation.value, optimisticConsultationPatch.value),
    )

    const isPlatformCoordBusy = computed(
      () =>
        isReserving.value ||
        isConfirming.value ||
        isReleasingReservation.value ||
        isWaitingForInsuredCoverage.value,
    )

    const isConsultationLifecycleStaleComputed = computed(() =>
      isConsultationLifecycleStale({
        consultationId: consultationId.value,
        hasActiveSubscription: isSubscribedToConsultation.value,
        snapshotLoading: isConsultationSnapshotLoading.value,
        hasServerSnapshot: serverConsultation.value != null,
        displayedLifecycleStatus: consultation.value?.lifecycleStatus,
        authoritativeLifecycleStatus: authoritativeServerLifecycleStatus.value,
      }),
    )

    const isBookingInteractionLocked = computed(() => {
      const platformOrResetBusy = isPlatformCoordBusy.value || isResettingSession.value
      const pendingBlocking =
        consultation.value?.lifecycleStatus === 'pending_noshow_fee' ||
        consultation.value?.lifecycleStatus === 'pending_settlement_payment'
      if (paymentComplete.value || customerCheckoutAcknowledged.value) {
        return platformOrResetBusy || pendingBlocking
      }
      return platformOrResetBusy || isConsultationLifecycleStaleComputed.value || pendingBlocking
    })

    const consultTypeLocked = computed(() => sessionConsultType.value != null)

    const currentStep = computed<ClinicalBookingStep>(() =>
      resolveClinicalBookingDisplayStep(consultation.value),
    )

    const draftSubPhase = computed<ClinicalDraftSubPhase>(() =>
      resolveDraftSubPhase({
        consultTypeLocked: consultTypeLocked.value || !!consultation.value?.consultType,
        consultTypeChosen: consultTypeChosen.value ?? consultation.value?.consultType ?? null,
      }),
    )

    const isInsuredPath = computed(
      () => billingPath.value === 'insured' || consultation.value?.billingPath === 'insured',
    )

    const insuredVisitStateForBridge = computed(() => resolveInsuredVisitStateForBridge())

    const activeConsultType = computed<ConsultationConsultType | null>(() => {
      return (
        consultTypeChosen.value ??
        consultation.value?.consultType ??
        sessionConsultType.value ??
        null
      )
    })

    const effectiveBookingConsultType = computed<ConsultationConsultType>(() => {
      return activeConsultType.value ?? resolveAutoConsultType()
    })

    const consultationSlotSelection = computed(() =>
      slotSelectionFromConsultation(consultation.value),
    )

    const selectedDate = computed(() => {
      if (currentStep.value === 'draft') {
        return userPickedDate.value
      }
      return userPickedDate.value ?? consultationSlotSelection.value?.date ?? null
    })

    const selectedSlot = computed(() => {
      if (currentStep.value === 'draft') {
        return userPickedSlot.value
      }
      return consultationSlotSelection.value?.slot ?? null
    })

    function clearLocalDraftPicksWithoutWrite() {
      userPickedDate.value = null
      userPickedSlot.value = null
      isDraftSlotReady.value = false
      remoteDraftSelectWriteAcknowledged.value = false
      acknowledgedRemoteDraftSelect = null
      slotSelectionError.value = null
      slotSelectionErrorSlotId.value = null
      outstandingLocalDraftSelectAction = null
    }

    function draftSelectViewMatchesSlot(
      entry: {
        tellescopeUserId: string
        startTimeInMS: number
        durationInMinutes: number
      },
      slot: AvailabilitySlot,
    ): boolean {
      const userId = slot.userIds[0]
      if (!userId) {
        return false
      }
      return (
        entry.tellescopeUserId === userId &&
        entry.startTimeInMS === slot.startTime.getTime() &&
        entry.durationInMinutes === slot.duration
      )
    }

    function availabilitySlotFromDraftSelectView(entry: {
      tellescopeUserId: string
      startTimeInMS: number
      durationInMinutes: number
    }): AvailabilitySlot | null {
      const block = availabilityStore.availabilitySlots.find(
        (cached) =>
          cached.userId === entry.tellescopeUserId &&
          cached.startTimeInMS === entry.startTimeInMS &&
          cached.durationInMinutes === entry.durationInMinutes,
      )
      if (!block) {
        return null
      }
      return {
        id: `${block.userId}:${block.startTimeInMS}:${block.durationInMinutes}`,
        startTime: new Date(block.startTimeInMS),
        endTime: new Date(block.startTimeInMS + block.durationInMinutes * 60_000),
        duration: block.durationInMinutes,
        available: true,
        userIds: [block.userId],
      }
    }

    function adoptRemoteDraftSelectEntry(entry: {
      tellescopeUserId: string
      startTimeInMS: number
      durationInMinutes: number
    }) {
      const slot = availabilitySlotFromDraftSelectView(entry)
      if (!slot) {
        clearLocalDraftPicksWithoutWrite()
        return
      }
      userPickedSlot.value = slot
      userPickedDate.value = slot.startTime
      isDraftSlotReady.value = true
      slotSelectionError.value = null
      slotSelectionErrorSlotId.value = null
      acknowledgedRemoteDraftSelect = {
        tellescopeUserId: entry.tellescopeUserId,
        startTimeInMS: entry.startTimeInMS,
        durationInMinutes: entry.durationInMinutes,
      }
      remoteDraftSelectWriteAcknowledged.value = true
    }

    function syncRemoteCustomerDraftSelect() {
      if (currentStep.value !== 'draft' || draftSubPhase.value !== 'slot-picker') {
        return
      }
      const customerId = customer.value?.id
      if (!customerId) {
        return
      }
      const remote = availabilityStore.getCustomerDraftSelect(customerId)
      if (!remote) {
        if (remoteDraftSelectWriteAcknowledged.value && userPickedSlot.value) {
          clearLocalDraftPicksWithoutWrite()
        }
        return
      }
      const local = userPickedSlot.value
      if (!remoteDraftSelectWriteAcknowledged.value) {
        if (local) {
          if (draftSelectViewMatchesSlot(remote, local)) {
            remoteDraftSelectWriteAcknowledged.value = true
            acknowledgedRemoteDraftSelect = {
              tellescopeUserId: remote.tellescopeUserId,
              startTimeInMS: remote.startTimeInMS,
              durationInMinutes: remote.durationInMinutes,
            }
            isDraftSlotReady.value = true
            outstandingLocalDraftSelectAction = null
          }
          return
        }
        if (outstandingLocalDraftSelectAction != null) {
          return
        }
        adoptRemoteDraftSelectEntry(remote)
        return
      }
      if (!local) {
        if (outstandingLocalDraftSelectAction != null) {
          return
        }
        adoptRemoteDraftSelectEntry(remote)
        return
      }
      const sameAsAck =
        acknowledgedRemoteDraftSelect != null &&
        acknowledgedRemoteDraftSelect.tellescopeUserId === remote.tellescopeUserId &&
        acknowledgedRemoteDraftSelect.startTimeInMS === remote.startTimeInMS &&
        acknowledgedRemoteDraftSelect.durationInMinutes === remote.durationInMinutes
      if (sameAsAck) {
        return
      }
      if (draftSelectViewMatchesSlot(remote, local)) {
        acknowledgedRemoteDraftSelect = {
          tellescopeUserId: remote.tellescopeUserId,
          startTimeInMS: remote.startTimeInMS,
          durationInMinutes: remote.durationInMinutes,
        }
        return
      }
      adoptRemoteDraftSelectEntry(remote)
    }

    const {
      updatedAt: availabilityUpdatedAt,
      consultType: availabilityConsultType,
      isSlotsFreshForVisit,
      slotPickerVisitError,
    } = storeToRefs(availabilityStore)

    /** The slot picker is on screen (not an earlier product step, not a torn-down session). */
    const isSlotPickerActive = computed(
      () =>
        customer.value != null &&
        !isSessionHydrating.value &&
        currentStep.value === 'draft' &&
        draftSubPhase.value === 'slot-picker' &&
        (productPreflowComplete.value || productStep.value == null),
    )

    // The picker waits for a slots doc the server vouched for during this visit. Later background
    // syncs update the doc in place without blanking the picker.
    const isCalendarSlotsReady = computed(
      () =>
        availabilityConsultType.value === effectiveBookingConsultType.value &&
        isSlotsFreshForVisit.value &&
        licensedStatesCacheLoaded.value,
    )

    // The error replaces the first-load spinner so a failed first sync is visible with Retry.
    const isAvailabilityLoading = computed(
      () => !availabilityUpdatedAt.value && !availabilityError.value,
    )

    const checkoutExpiresAt = computed(() => checkoutExpiresAtFromConsultation(consultation.value))

    function isCheckoutHoldLifecycleStatus(
      status: Consultation['lifecycleStatus'] | undefined,
    ): boolean {
      return status === 'reserved' || status === 'payment_pending'
    }

    const isClinicalHoldActive = computed(() => {
      const status = consultation.value?.lifecycleStatus
      return (
        isCheckoutHoldLifecycleStatus(status) &&
        checkoutExpiresAt.value != null &&
        !clinicalHoldCapReached.value
      )
    })

    const paymentComplete = computed(() => isPaymentComplete(consultation.value))

    const settlementPaymentComplete = computed(() =>
      isSettlementPaymentComplete(consultation.value),
    )

    function clearCheckoutPropagationTimer() {
      if (checkoutPropagationTimeoutId != null) {
        window.clearTimeout(checkoutPropagationTimeoutId)
        checkoutPropagationTimeoutId = null
      }
    }

    function clearCheckoutPropagationState() {
      clearCheckoutPropagationTimer()
      customerAwaitingCheckoutPropagation.value = false
      customerCheckoutPropagationError.value = null
    }

    watch(paymentComplete, (complete) => {
      if (complete) {
        clearCheckoutPropagationState()
        if (consultationId.value) {
          clearClinicalCheckoutSubmitted(consultationId.value)
        }
      }
    })

    watch(
      [currentStep, consultationId, isInsuredPath, chargeFullListPrice],
      ([step, id, insured, fullPrice]) => {
        if (shouldSubscribeBridgeCoverageForCopay(step) && id && insured && !fullPrice) {
          subscribeBridgeCoverageForPaymentPending(id)
          return
        }
        unsubscribeBridgeCoverageProjectionSnapshot()
      },
      { immediate: true },
    )

    watch(
      [currentStep, consultationId],
      ([step, id]) => {
        const projectionKey = resolvePayStepStripeCheckoutProjectionKey(step)
        if (projectionKey && id) {
          subscribePayStepStripeCheckoutProjection(id, projectionKey)
          return
        }
        unsubscribePayStepStripeCheckoutProjectionSnapshot()
      },
      { immediate: true },
    )

    watch(
      [isInsuredPath, () => customer.value?.id, activeBridgePolicyId, insuredPayerDisplayName],
      ([insured, customerId, policyId, payerDisplayName]) => {
        if (!insured || !customerId) {
          unsubscribeBridgePolicySnapshot()
          unsubscribeBridgePatientProjectionSnapshot()
          return
        }
        if (payerDisplayName) {
          unsubscribeBridgePolicySnapshot()
          unsubscribeBridgePatientProjectionSnapshot()
          return
        }
        if (policyId) {
          unsubscribeBridgePatientProjectionSnapshot()
          hydratePayerFromBridgePolicy(policyId)
          return
        }
        subscribeBridgePatientForPolicyHydration(customerId as CustomerId)
      },
      { immediate: true },
    )

    onScopeDispose(() => {
      unsubscribeBridgeCoverageProjectionSnapshot()
      unsubscribeBridgePolicySnapshot()
      unsubscribeBridgePatientProjectionSnapshot()
    })

    watch(
      [currentStep, checkoutExpiresAt],
      () => {
        if (countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
        const holdStep = currentStep.value === 'reserved' || currentStep.value === 'payment_pending'
        if (!holdStep || !checkoutExpiresAt.value) {
          return
        }
        now.value = Date.now()
        countdownTimer = setInterval(() => {
          now.value = Date.now()
        }, 1000)
      },
      { immediate: true },
    )

    const holdMinutesRemaining = computed(() => {
      const holdStep = currentStep.value === 'reserved' || currentStep.value === 'payment_pending'
      if (!holdStep || !checkoutExpiresAt.value) {
        return null
      }
      return remainingClinicalHoldMinutes(checkoutExpiresAt.value, new Date(now.value))
    })

    const holdDisplayMessage = computed(() => {
      const holdStep = currentStep.value === 'reserved' || currentStep.value === 'payment_pending'
      if (!holdStep || !checkoutExpiresAt.value) {
        return null
      }
      const message = formatClinicalCheckoutHoldMessage(
        checkoutExpiresAt.value,
        new Date(now.value),
        clinicalBookingTimezone.value,
      )
      return message === 'expired' ? null : message
    })

    function unsubscribeConsultationSnapshot() {
      if (unsubscribeConsultation) {
        unsubscribeConsultation()
        unsubscribeConsultation = null
      }
      isSubscribedToConsultation.value = false
      isConsultationSnapshotLoading.value = false
      unsubscribeBridgeCoverageProjectionSnapshot()
      unsubscribeBridgePolicySnapshot()
      unsubscribeBridgePatientProjectionSnapshot()
      activeBridgePolicyId.value = null
    }

    function applyServerConsultation(parsed: Consultation, options?: { fromCache?: boolean }) {
      serverConsultation.value = parsed
      if (!options?.fromCache) {
        authoritativeServerLifecycleStatus.value = parsed.lifecycleStatus
      }
      if (shouldClearOptimisticPatch(parsed, optimisticConsultationPatch.value)) {
        optimisticConsultationPatch.value = null
      }
    }

    function waitForAuthoritativeLifecycleStatus(
      status: Consultation['lifecycleStatus'],
      timeoutMs = 15_000,
    ): Promise<boolean> {
      if (authoritativeServerLifecycleStatus.value === status) {
        return Promise.resolve(true)
      }
      return new Promise((resolve) => {
        const stop = watch(authoritativeServerLifecycleStatus, (lifecycleStatus) => {
          if (lifecycleStatus === status) {
            stop()
            clearTimeout(timeoutId)
            resolve(true)
          }
        })
        const timeoutId = window.setTimeout(() => {
          stop()
          resolve(false)
        }, timeoutMs)
      })
    }

    function waitForInitialConsultationSnapshot(
      expectedId: string,
      sessionGen: number,
      timeoutMs = 15_000,
    ): Promise<Consultation | null> {
      if (
        sessionGen === operationGeneration &&
        !isConsultationSnapshotLoading.value &&
        serverConsultation.value?.id === expectedId
      ) {
        return Promise.resolve(serverConsultation.value)
      }
      return new Promise((resolve) => {
        const stop = watch(
          [serverConsultation, isConsultationSnapshotLoading],
          () => {
            if (sessionGen !== operationGeneration) {
              stop()
              clearTimeout(timeoutId)
              resolve(null)
              return
            }
            if (isConsultationSnapshotLoading.value) {
              return
            }
            stop()
            clearTimeout(timeoutId)
            if (serverConsultation.value?.id === expectedId) {
              resolve(serverConsultation.value)
              return
            }
            resolve(null)
          },
          { immediate: true },
        )
        const timeoutId = window.setTimeout(() => {
          stop()
          resolve(null)
        }, timeoutMs)
      })
    }

    async function abortNonContinuableContinueSession(sessionGen: number) {
      if (sessionGen !== operationGeneration) {
        return
      }
      const recoverFreshSignupSession = entrySource.value === 'signup'
      if (!recoverFreshSignupSession) {
        toastStore.error('This consultation can no longer be continued.')
      }
      unsubscribeConsultationSnapshot()
      preferredConsultationId.value = null
      sessionConsultType.value = null
      consultTypeChosen.value = null
      serverConsultation.value = null
      optimisticConsultationPatch.value = null
      authoritativeServerLifecycleStatus.value = null
      consultationId.value = null
      activeSessionKey = null

      if (!recoverFreshSignupSession || !customer.value) {
        return
      }

      hydrateProfileDraftFromCustomer(customer.value)
      if (customer.value.id) {
        try {
          const hasScheduled = await loadHasScheduledConsultation(
            customer.value.id,
            preferredConsultationId.value,
          )
          if (sessionGen !== operationGeneration) {
            return
          }
          hasScheduledConsultation.value = hasScheduled
        } catch (err) {
          logger.warn('Failed to check scheduled consultations after abort', { err })
          if (sessionGen !== operationGeneration) {
            return
          }
          hasScheduledConsultation.value = false
        }
      }
      if (sessionGen !== operationGeneration) {
        return
      }
      await beginCustomerProductPreflow()
    }

    async function ensureAuthoritativeReservedBeforeConfirm(): Promise<boolean> {
      if (authoritativeServerLifecycleStatus.value === 'reserved') {
        return true
      }
      if (!unsubscribeConsultation) {
        return true
      }

      const ready = await waitForAuthoritativeLifecycleStatus('reserved')
      if (ready) {
        return true
      }

      const staleReservedUi =
        currentStep.value === 'reserved' &&
        (authoritativeServerLifecycleStatus.value === 'draft' ||
          authoritativeServerLifecycleStatus.value === null)
      if (!staleReservedUi) {
        return false
      }

      const reReserved = await reserveSlot()
      if (!reReserved) {
        return false
      }
      return waitForAuthoritativeLifecycleStatus('reserved')
    }

    function subscribeConsultation(id: string) {
      unsubscribeConsultationSnapshot()
      consultationId.value = id
      isSubscribedToConsultation.value = true
      isConsultationSnapshotLoading.value = true
      const refDoc = doc(firestore, 'consultations', id)
      unsubscribeConsultation = onSnapshot(
        refDoc,
        { includeMetadataChanges: true },
        (snap) => {
          if (!snap.exists()) {
            serverConsultation.value = null
            isConsultationSnapshotLoading.value = false
            return
          }
          const data = snap.data() as Record<string, unknown>
          const parsed = consultationSchema.safeParse({
            id: snap.id,
            ...consultationSnapshotFieldsToDates(data),
          })
          if (!parsed.success) {
            logger.error('Failed to parse consultation snapshot', {
              id,
              error: parsed.error,
            })
            isConsultationSnapshotLoading.value = false
            return
          }
          applyServerConsultation(parsed.data, {
            fromCache: !!snap.metadata?.fromCache && parsed.data.lifecycleStatus !== 'draft',
          })
          isConsultationSnapshotLoading.value = false
        },
        (err) => {
          logger.error('Consultation snapshot error', { id, err })
          error.value = err instanceof Error ? err.message : 'Failed to load consultation.'
          isConsultationSnapshotLoading.value = false
        },
      )
    }

    function resolveClinicalBookingVisitState(): string | null {
      return (
        resolveInsuredVisitStateForBridge() ?? normalizeInsuredVisitState(customer.value?.state)
      )
    }

    const clinicalBookingVisitState = computed(() => resolveClinicalBookingVisitState())

    const clinicalBookingTimezone = computed(() =>
      getTimezoneForStateName(clinicalBookingVisitState.value ?? customer.value?.state),
    )

    async function bootstrapAvailability(options?: {
      ping?: boolean
      fromSlotPickerVisit?: boolean
    }) {
      const consultType = effectiveBookingConsultType.value
      if (availabilityStore.consultType !== consultType) {
        if (isSlotPickerActive.value && !options?.fromSlotPickerVisit) {
          await setConsultType(consultType)
          return
        }
        availabilityStore.setConsultType(consultType)
      }
      availabilityStore.setFilterStateCode(resolveClinicalBookingVisitState())
      if (!availabilityStore.isSubscribed) {
        await availabilityStore.subscribe()
      }
      if (options?.ping) {
        // Best effort: entering the slot picker pings again and owns the visible error.
        await availabilityStore.pingFreshness().catch((err) => {
          logger.warn('Failed to ping clinical availability', { err })
        })
      }
    }

    /**
     * Never rejects and never writes `error`: only the slot picker consumes availability, so a
     * subscribe failure must not replace earlier steps. Callers never wait on this.
     */
    function startAvailabilityBootstrap(options?: { ping?: boolean }): Promise<void> {
      const gen = operationGeneration
      return bootstrapAvailability(options).then(
        () => undefined,
        (err) => {
          if (gen !== operationGeneration) {
            return
          }
          logger.warn('Failed to load clinical availability', { err })
          availabilityError.value =
            err instanceof Error ? err.message : 'Failed to load availability.'
        },
      )
    }

    /**
     * Slot picker opened (or retried): resolves once the slots doc reflects a sync the server
     * considers fresh. Never rejects; a bounded wait or repeated failures set `availabilityError`.
     */
    async function runSlotPickerVisit(options?: { force?: boolean }): Promise<void> {
      const gen = operationGeneration
      try {
        await bootstrapAvailability({ fromSlotPickerVisit: true })
        const ready = await availabilityStore.beginSlotPickerVisit(options)
        if (ready && gen === operationGeneration) {
          availabilityError.value = null
          if (licensedStatesLoadFailed.value && !licensedStatesCacheLoaded.value) {
            availabilityError.value = LICENSED_STATES_LOAD_ERROR_MESSAGE
          }
        }
      } catch (err) {
        if (gen !== operationGeneration) {
          return
        }
        logger.warn('Failed to load clinical availability', { err })
        availabilityError.value =
          err instanceof Error ? err.message : 'Failed to load availability.'
      }
    }

    async function prefetchSessionAvailability(gen: number): Promise<void> {
      await followupContextReady
      if (gen !== operationGeneration) {
        return
      }
      if (!sessionConsultType.value && !consultTypeChosen.value) {
        const autoConsultType = resolveAutoConsultType()
        sessionConsultType.value = autoConsultType
        consultTypeChosen.value = autoConsultType
        if (availabilityStore.consultType !== autoConsultType) {
          availabilityStore.setConsultType(autoConsultType)
        }
      }
      await startAvailabilityBootstrap({ ping: true })
    }

    async function probeScheduledConsultation(
      customerId: string,
      sessionConsultationId: string | null,
      sessionGen: number,
    ): Promise<boolean> {
      try {
        return await loadHasScheduledConsultation(customerId, sessionConsultationId, sessionGen)
      } catch (err) {
        logger.warn('Failed to check scheduled consultations', { err })
        return false
      }
    }

    async function startSession(args: StartClinicalBookingSessionArgs) {
      const sessionKey = buildSessionKey(args)
      const nextConsultationId = args.consultationId ?? null

      const effectiveRequestedConsultType = args.consultType ?? sessionConsultType.value ?? null
      const isSameActiveSession =
        activeSessionKey === sessionKey &&
        customer.value?.id === args.customer.id &&
        entrySource.value === (args.entrySource ?? 'account') &&
        preferredConsultationId.value === nextConsultationId &&
        sessionConsultType.value === effectiveRequestedConsultType &&
        (nextConsultationId === null ||
          (consultationId.value === nextConsultationId && serverConsultation.value != null))

      if (isSameActiveSession) {
        sessionAttachEpoch += 1
        void ensureLicensedStatesLoaded()
        const previousState = customer.value?.state
        customer.value = args.customer
        mergeProfileDraftFromCustomer(args.customer)
        if (
          previousState !== args.customer.state &&
          (sessionConsultType.value || consultTypeChosen.value)
        ) {
          await startAvailabilityBootstrap()
        }
        return
      }

      const gen = ++operationGeneration
      sessionAttachEpoch += 1

      const idToRelease = consultationId.value
      const statusToRelease = lifecycleStatusForRelease()
      const customerCheckoutSubmitted =
        customerCheckoutAcknowledged.value ||
        customerAwaitingCheckoutPropagation.value ||
        (idToRelease != null && isClinicalCheckoutRecentlySubmitted(idToRelease))
      const shouldReleaseHold =
        !customerCheckoutSubmitted &&
        shouldAttemptClinicalRelease({
          status: statusToRelease,
          customerCheckoutSubmitted,
        }) &&
        idToRelease != null &&
        idToRelease !== nextConsultationId
      if (shouldReleaseHold) {
        void cancelClinicalHoldFromSnapshot({
          consultationId: idToRelease,
          status: statusToRelease,
          customerCheckoutSubmitted,
          reason: 'customer_aborted',
        })
      }

      activeSessionKey = sessionKey

      isSessionHydrating.value = true
      let sessionPrefetches: Promise<unknown> = Promise.resolve()

      try {
        const licensedStatesPrefetch = ensureLicensedStatesLoaded()
        unsubscribeConsultationSnapshot()
        resetLocalPicks()
        entrySource.value = args.entrySource ?? 'account'
        customer.value = args.customer
        followupBookingEligible.value = false
        preferredFollowupTellescopeUserId.value = null
        followupInsurancePolicyLocked.value = false
        followupBookingContextLoaded = false
        followupContextReady = ensureFollowupBookingContext(gen)
        const scheduledProbe =
          args.freshClinicalBooking || !args.customer.id
            ? null
            : probeScheduledConsultation(args.customer.id, nextConsultationId, gen)
        captureProfileDemographicsBaseline(args.customer)
        preferredConsultationId.value = nextConsultationId
        sessionConsultType.value = args.consultType ?? null
        consultTypeChosen.value = args.consultType ?? null
        customerCheckoutAcknowledged.value = false
        clearCheckoutPropagationState()
        error.value = null
        serverConsultation.value = null
        optimisticConsultationPatch.value = null
        authoritativeServerLifecycleStatus.value = null
        consultationId.value = null
        if (!nextConsultationId) {
          isConsultationSnapshotLoading.value = false
        }

        if (gen !== operationGeneration) {
          return
        }

        let loadedConsultation: Consultation | null = null
        if (nextConsultationId) {
          subscribeConsultation(nextConsultationId)
          loadedConsultation = await waitForInitialConsultationSnapshot(nextConsultationId, gen)
          if (gen !== operationGeneration) {
            return
          }
          if (
            loadedConsultation &&
            (loadedConsultation.lifecycleStatus === 'cancelled' ||
              loadedConsultation.lifecycleStatus === 'cancel_failed')
          ) {
            await abortNonContinuableContinueSession(gen)
            return
          }
          if (loadedConsultation?.billingPath) {
            billingPath.value = loadedConsultation.billingPath
          }
          if (loadedConsultation?.billingPath === 'insured' || billingPath.value === 'insured') {
            applyPersistedInsuredDisplay(args.customer.id)
          }
          reservedSubPhase.value = resolveReservedSubPhaseForResume({
            billingPath: loadedConsultation?.billingPath ?? billingPath.value,
            lifecycleStatus: loadedConsultation?.lifecycleStatus,
            insuredCoverageAcknowledged: false,
          })
          if (loadedConsultation?.consultType && !consultTypeChosen.value) {
            consultTypeChosen.value = loadedConsultation.consultType
          }
        }

        sessionPrefetches = Promise.all([licensedStatesPrefetch, prefetchSessionAvailability(gen)])

        const hasScheduled = scheduledProbe ? await scheduledProbe : false
        if (gen !== operationGeneration) {
          return
        }
        hasScheduledConsultation.value = hasScheduled
        if (!hasScheduled) {
          scheduledConsultation.value = null
        }

        hydrateProfileDraftFromCustomer(args.customer)

        if (!nextConsultationId) {
          await beginCustomerProductPreflow()
          if (args.initialProductStep && isProductPreflowStep(args.initialProductStep)) {
            productStep.value = args.initialProductStep
            productPreflowComplete.value = false
            applyPersistedInsuredDisplay(args.customer.id)
          }
        } else {
          const shouldRestoreInsuranceVerifiedInterstitial =
            args.initialProductStep === 'insurance_verified' &&
            loadedConsultation?.lifecycleStatus === 'draft' &&
            (loadedConsultation?.billingPath ?? billingPath.value) === 'insured'

          if (shouldRestoreInsuranceVerifiedInterstitial) {
            productStep.value = 'insurance_verified'
            productPreflowComplete.value = false
            applyPersistedInsuredDisplay(args.customer.id)
          } else {
            productPreflowComplete.value = true
            productStep.value = null
          }
          if (
            loadedConsultation &&
            shouldAutoConfirmReservedSelfPayOnResume({
              lifecycleStatus: loadedConsultation.lifecycleStatus,
              billingPath: loadedConsultation.billingPath ?? billingPath.value,
            })
          ) {
            reservedSubPhase.value = null
            await confirmBooking()
          }
        }
      } finally {
        if (gen === operationGeneration) {
          isSessionHydrating.value = false
        }
      }
      // The UI is interactive here; awaiting callers also observe settled session prefetches.
      await sessionPrefetches
    }

    watch(slotPickerVisitError, (err, prevErr) => {
      if (!isSlotPickerActive.value) {
        return
      }
      if (err) {
        availabilityError.value = err
      } else if (prevErr && availabilityError.value === prevErr) {
        availabilityError.value = null
      }
    })

    watch(licensedStatesLoadFailed, (failed) => {
      if (failed && isSlotPickerActive.value && !licensedStatesCacheLoaded.value) {
        availabilityError.value = LICENSED_STATES_LOAD_ERROR_MESSAGE
      }
    })

    watch(isSlotPickerActive, (active) => {
      if (active) {
        if (licensedStatesLoadFailed.value && !licensedStatesCacheLoaded.value) {
          availabilityError.value = LICENSED_STATES_LOAD_ERROR_MESSAGE
        }
        void runSlotPickerVisit()
        syncRemoteCustomerDraftSelect()
      } else {
        availabilityStore.endSlotPickerVisit()
        availabilityError.value = null
      }
    })

    watch(
      () => availabilityStore.clinicalCustomerDraftSelects,
      () => {
        syncRemoteCustomerDraftSelect()
      },
      { deep: true },
    )

    watch(
      () =>
        authoritativeServerLifecycleStatus.value ??
        serverConsultation.value?.lifecycleStatus ??
        null,
      (status, previous) => {
        if (previous === 'draft' && status && status !== 'draft') {
          clearLocalDraftPicksWithoutWrite()
        }
      },
    )

    async function setConsultType(next: ConsultationConsultType) {
      const consultTypeChanged = availabilityStore.consultType !== next
      consultTypeChosen.value = next
      availabilityStore.setConsultType(next)
      userPickedDate.value = null
      await selectSlot(null)
      if (consultTypeChanged && availabilityStore.isSubscribed) {
        if (isSlotPickerActive.value) {
          void runSlotPickerVisit()
        } else {
          void startAvailabilityBootstrap({ ping: true })
        }
      }
    }

    /**
     * The picked date applies immediately; clearing the slot only waits on the local state. The
     * draft-select unmark is queued on `selectSlotDraftWriteChain`, so a later pick's mark write
     * always lands after it.
     */
    function selectDate(date: Date | null): void {
      void selectSlot(null)
      userPickedDate.value = date
    }

    function isSlotStillBookable(slot: AvailabilitySlot): boolean {
      const userId = slot.userIds[0]
      if (!userId) return false
      const startTimeInMS = slot.startTime.getTime()
      const inAvailability = availabilityStore.availabilitySlots.some(
        (cached) =>
          cached.userId === userId &&
          cached.startTimeInMS === startTimeInMS &&
          cached.durationInMinutes === slot.duration,
      )
      if (!inAvailability) return false

      return !hasOverlappingClinicalDraftSelect({
        customerDrafts: availabilityStore.clinicalCustomerDraftSelects,
        tellescopeUserId: userId,
        startTimeInMS,
        durationInMinutes: slot.duration,
        ignoreCustomerId: customer.value?.id,
      })
    }

    async function selectSlot(slot: AvailabilitySlot | null): Promise<void> {
      const generation = ++selectSlotGeneration
      const slotSelectionFailureMessage =
        'Something went wrong with selecting a slot. please choose again'

      const applySelectionFailure = (failedSlotId: string | null) => {
        if (generation !== selectSlotGeneration) {
          return
        }
        userPickedSlot.value = null
        isDraftSlotReady.value = false
        slotSelectionError.value = slotSelectionFailureMessage
        slotSelectionErrorSlotId.value = failedSlotId
        outstandingLocalDraftSelectAction = null
      }

      const applySelectionSuccess = () => {
        if (generation !== selectSlotGeneration) {
          return
        }
        isDraftSlotReady.value = true
        slotSelectionError.value = null
        slotSelectionErrorSlotId.value = null
        outstandingLocalDraftSelectAction = null
      }

      let shouldUnmarkDraftOnClear = false
      if (!slot) {
        const customerId = customer.value?.id
        shouldUnmarkDraftOnClear = Boolean(
          customerId &&
          (userPickedSlot.value !== null ||
            availabilityStore.clinicalCustomerDraftSelects[customerId]),
        )
        userPickedSlot.value = null
        isDraftSlotReady.value = false
        slotSelectionError.value = null
        slotSelectionErrorSlotId.value = null
        if (shouldUnmarkDraftOnClear) {
          outstandingLocalDraftSelectAction = 'clear'
        }
      } else if (
        userPickedSlot.value?.id === slot.id &&
        (isDraftSlotReady.value || isMarkingDraftSlot.value)
      ) {
        return
      } else if (currentStep.value !== 'draft' || !customer.value?.id) {
        userPickedSlot.value = slot
        isDraftSlotReady.value = true
        slotSelectionError.value = null
        slotSelectionErrorSlotId.value = null
      } else {
        userPickedSlot.value = slot
        isDraftSlotReady.value = false
        remoteDraftSelectWriteAcknowledged.value = false
        acknowledgedRemoteDraftSelect = null
        slotSelectionError.value = null
        slotSelectionErrorSlotId.value = null
        outstandingLocalDraftSelectAction = 'write'
      }

      const run = async () => {
        const customerId = customer.value?.id
        if (!slot) {
          if (shouldUnmarkDraftOnClear && customerId) {
            try {
              await availabilityStore.unmarkSlotAsSelected(customerId)
            } catch (err) {
              logger.error('Failed to unmark clinical draft slot', { customerId, err })
            } finally {
              if (outstandingLocalDraftSelectAction === 'clear') {
                outstandingLocalDraftSelectAction = null
              }
            }
          } else if (outstandingLocalDraftSelectAction === 'clear') {
            outstandingLocalDraftSelectAction = null
          }
          return
        }

        if (userPickedSlot.value?.id === slot.id && isDraftSlotReady.value) {
          return
        }

        if (currentStep.value !== 'draft' || !customerId) {
          return
        }

        const userId = slot.userIds[0]
        if (!userId) {
          logger.error('Cannot mark clinical draft select — slot has no Tellescope user', {
            slotId: slot.id,
          })
          applySelectionFailure(slot.id)
          return
        }

        isMarkingDraftSlot.value = true
        try {
          await availabilityStore.makeSlotAsSelected({
            customerId,
            startTimeInMS: slot.startTime.getTime(),
            endTimeInMS: slot.endTime.getTime(),
            tellescopeUserId: userId,
          })
          if (generation !== selectSlotGeneration) {
            return
          }
          if (!isSlotStillBookable(slot)) {
            try {
              await availabilityStore.unmarkSlotAsSelected(customerId)
            } catch (err) {
              logger.error('Failed to unmark clinical draft after slot taken', { customerId, err })
            }
            applySelectionFailure(slot.id)
            return
          }
          applySelectionSuccess()
        } catch (err) {
          logger.error('Failed to mark clinical draft slot', { customerId, err })
          try {
            await availabilityStore.unmarkSlotAsSelected(customerId)
          } catch (unmarkErr) {
            logger.error('Failed to unmark slot after selection failure', { customerId, unmarkErr })
          }
          applySelectionFailure(slot.id)
        } finally {
          isMarkingDraftSlot.value = false
        }
      }

      const chained = selectSlotDraftWriteChain.then(run, run)
      selectSlotDraftWriteChain = chained.catch(() => undefined)
      await chained
    }

    async function clearConsultTypeChoice() {
      if (consultTypeLocked.value) return
      consultTypeChosen.value = null
      userPickedDate.value = null
      await selectSlot(null)
    }

    async function reserveSlot(): Promise<boolean> {
      if (isReserving.value || isBookingInteractionLocked.value || availabilityError.value) {
        return false
      }
      if (!customer.value || !userPickedSlot.value || !activeConsultType.value) {
        if (!activeConsultType.value) {
          toastStore.error('Please select a consultation type first.')
        }
        return false
      }
      isReserving.value = true
      try {
        // Continue stays disabled until the draft-select write completes (R-18 gating).
        await selectSlotDraftWriteChain
        if (!customer.value || !userPickedSlot.value || !isDraftSlotReady.value) {
          if (userPickedSlot.value && !isDraftSlotReady.value) {
            toastStore.error('Please choose a time.')
          }
          return false
        }
        const pickedSlot = userPickedSlot.value
        const userId = pickedSlot.userIds[0]
        if (!userId) {
          toastStore.error('Selected slot has no Tellescope user')
          return false
        }
        const existingConsultationId = resolveConsultationIdForReserve({
          preferredConsultationId: preferredConsultationId.value,
          activeConsultationId: consultationId.value,
          consultation: consultation.value,
        })
        if (!existingConsultationId) {
          toastStore.error('Please complete booking setup before selecting a slot.')
          return false
        }
        const reserveArgs = {
          customerId: customer.value.id as CustomerId,
          consultationId: existingConsultationId,
          consultType: activeConsultType.value,
          tellescopeUserId: userId,
          startTimeInMS: pickedSlot.startTime.getTime(),
          durationInMinutes: pickedSlot.duration,
        }
        const result = await invokePlatformCoord('reserveClinicalSlot', reserveArgs, () =>
          platformCoordApi.reserveClinicalSlot(reserveArgs),
        )
        const expiresAt = new Date(result.checkoutExpiresAt)
        if (Number.isNaN(expiresAt.getTime())) {
          throw new Error('Invalid hold expiry from reserveClinicalSlot')
        }
        const reservedSlot = pickedSlot
        optimisticConsultationPatch.value = buildReservedConsultationPatch({
          consultationId: result.consultationId as ConsultationId,
          customerId: customer.value.id as CustomerId,
          consultType: activeConsultType.value,
          existing: consultation.value,
          reservedSlot,
          tellescopeUserId: userId,
          checkoutExpiresAt: expiresAt,
          reservedSlotKey: result.reservedSlotKey,
        })
        subscribeConsultation(result.consultationId)
        authoritativeServerLifecycleStatus.value = 'reserved'
        resetInsuredCoverageState()
        chargeFullListPrice.value = false
        insuredPaymentFailureReason.value = null

        if (isInsuredPath.value) {
          reservedSubPhase.value = 'insured_coverage'
          insuredCoveragePhase.value = 'checking_eligibility'
        } else {
          reservedSubPhase.value = null
          const confirmed = await confirmBooking()
          if (!confirmed) {
            await goBackToDraft()
            return false
          }
        }
        const customerId = customer.value?.id
        if (customerId) {
          const unmarkAfterReserve = () =>
            availabilityStore.unmarkSlotAsSelected(customerId).catch((err) => {
              logger.error('Failed to unmark clinical draft after reserve', {
                customerId,
                err,
              })
            })
          selectSlotDraftWriteChain = selectSlotDraftWriteChain.then(
            unmarkAfterReserve,
            unmarkAfterReserve,
          )
        }
        return true
      } catch (err) {
        logger.error('Failed to reserve clinical slot', { err })
        if (isClinicalReserveContinuedInAnotherWindowError(err)) {
          toastStore.error('This booking was continued in another window.')
          return false
        }
        if (isClinicalSlotTakenError(err)) {
          const takenSlot = userPickedSlot.value
          const takenSlotId = takenSlot?.id ?? null
          const takenConsultType = activeConsultType.value
          const takenUserId = takenSlot?.userIds[0]
          if (takenSlot && takenUserId) {
            availabilityStore.hideSlotForSession({
              tellescopeUserId: takenUserId,
              startTimeInMS: takenSlot.startTime.getTime(),
              durationInMinutes: takenSlot.duration,
            })
          }
          await selectSlot(null)
          slotSelectionError.value = 'That time was just taken. Please choose another time.'
          slotSelectionErrorSlotId.value = takenSlotId
          // Slots are trusted until the server refreshes them; ask for that refresh now.
          void availabilityStore
            .pingFreshness(
              takenConsultType ? { consultType: takenConsultType, force: true } : { force: true },
            )
            .catch((pingErr) => {
              logger.warn('Failed to ping clinical availability after slot taken', { pingErr })
            })
          return false
        }
        toastUnlessClinicalBookingInvalidated(
          err,
          'Failed to reserve slot. Please try another time.',
        )
        return false
      } finally {
        isReserving.value = false
      }
    }

    /**
     * Confirm reserved slot. UI gates on projection wait (`insuredCoveragePhase`);
     * `confirmClinicalBooking` independently validates observed SE ELIGIBLE server-side.
     */
    async function confirmBooking(options?: ConfirmBookingOptions): Promise<boolean> {
      const id = consultationId.value
      if (!id) {
        toastStore.error('No reserved consultation — select a slot again.')
        return false
      }
      const convertingToSelfPay = options?.billingPath === 'self_pay'
      if (isInsuredPath.value && !convertingToSelfPay) {
        const coverageReady = insuredCoveragePhase.value === 'ready'
        const fullPriceReady = insuredCoveragePhase.value === 'full_price_required'
        if (!coverageReady && !fullPriceReady) {
          toastStore.error('Complete coverage verification before continuing to payment.')
          return false
        }
      }
      isConfirming.value = true
      try {
        const reserved = await ensureAuthoritativeReservedBeforeConfirm()
        if (!reserved) {
          toastStore.error(
            'Your reservation is not active on the server. Please go back and reserve the slot again.',
          )
          return false
        }
        const confirmArgs = {
          consultationId: id as ConsultationId,
          ...(convertingToSelfPay ? { billingPath: 'self_pay' as const } : {}),
        }
        const result = await invokePlatformCoord('confirmClinicalBooking', confirmArgs, () =>
          platformCoordApi.confirmClinicalBooking(confirmArgs),
        )
        customerCheckoutAcknowledged.value = false
        optimisticConsultationPatch.value = {
          ...buildConfirmBookingPatch(consultation.value, result.lifecycleStatus),
          ...(convertingToSelfPay ? { billingPath: 'self_pay' } : {}),
        }
        toastStore.debug('Slot confirmed — complete payment below.')
        if (convertingToSelfPay) {
          setBillingPath('self_pay')
        }
        return true
      } catch (err: unknown) {
        logger.error('Error confirming clinical booking:', err)
        toastUnlessClinicalBookingInvalidated(err, 'Something went wrong confirming the booking.')
        return false
      } finally {
        isConfirming.value = false
      }
    }

    async function extendClinicalHold(): Promise<void> {
      if (!consultationId.value || clinicalHoldCapReached.value || isExtendingHold.value) {
        return
      }
      const status = consultation.value?.lifecycleStatus
      if (!isCheckoutHoldLifecycleStatus(status) || !checkoutExpiresAt.value) {
        return
      }

      isExtendingHold.value = true
      const extendArgs = {
        consultationId: consultationId.value as ConsultationId,
      }
      try {
        const result = await invokePlatformCoord('extendClinicalHold', extendArgs, () =>
          platformCoordApi.extendClinicalHold(extendArgs),
        )
        const expiresAt = new Date(result.checkoutExpiresAt)
        if (Number.isNaN(expiresAt.getTime())) {
          throw new Error('Invalid hold expiry from extendClinicalHold')
        }
        const currentExpiry = checkoutExpiresAt.value
        if (!currentExpiry || expiresAt.getTime() > currentExpiry.getTime()) {
          optimisticConsultationPatch.value = {
            ...(optimisticConsultationPatch.value ?? {}),
            checkoutExpiresAt: expiresAt,
          }
        }
        if (result.holdCapReached) {
          clinicalHoldCapReached.value = true
        }
      } catch (err) {
        if (
          err instanceof Error &&
          (/expired/i.test(err.message) || /extension cap reached/i.test(err.message))
        ) {
          clinicalHoldCapReached.value = true
        }
        throw err
      } finally {
        isExtendingHold.value = false
      }
    }

    async function goBackToDraft() {
      resetInsuredCoverageState()
      reservedSubPhase.value = null
      const heldOnServer =
        consultation.value?.lifecycleStatus === 'reserved' ||
        authoritativeServerLifecycleStatus.value === 'reserved'
      if (consultationId.value && heldOnServer) {
        activeReleaseCount.value += 1
        try {
          const returnToDraftArgs = {
            consultationId: consultationId.value as ConsultationId,
          }
          await invokePlatformCoord('returnClinicalConsultationToDraft', returnToDraftArgs, () =>
            platformCoordApi.returnClinicalConsultationToDraft(returnToDraftArgs),
          )
        } catch (err) {
          logger.warn('Failed to return clinical consultation to draft on back', { err })
        } finally {
          activeReleaseCount.value -= 1
        }
      }
      if (heldOnServer) {
        optimisticConsultationPatch.value = {
          lifecycleStatus: 'draft',
          startTimeInMS: null,
          durationMinutes: null,
          scheduledStart: null,
          scheduledEnd: null,
        } as unknown as Partial<Consultation>
        authoritativeServerLifecycleStatus.value = 'draft'
      }
    }

    function lifecycleStatusForRelease(): Consultation['lifecycleStatus'] | undefined {
      if (serverConsultation.value) {
        return serverConsultation.value.lifecycleStatus
      }
      return consultation.value?.lifecycleStatus
    }

    function isCustomerCheckoutSubmitted(): boolean {
      if (customerCheckoutAcknowledged.value || customerAwaitingCheckoutPropagation.value) {
        return true
      }
      const id = consultationId.value
      return id != null && isClinicalCheckoutRecentlySubmitted(id)
    }

    function captureCancelHoldSnapshot(
      customerCheckoutSubmitted?: boolean,
      reason: CancellationReason = 'customer_aborted',
    ): CancelHoldSnapshot | null {
      const id = consultationId.value
      if (!id) {
        return null
      }
      return {
        consultationId: id,
        status: lifecycleStatusForRelease(),
        customerCheckoutSubmitted: customerCheckoutSubmitted ?? isCustomerCheckoutSubmitted(),
        reason,
      }
    }

    async function cancelClinicalHoldFromSnapshot(
      snapshot: CancelHoldSnapshot | null,
    ): Promise<void> {
      if (!snapshot) {
        return
      }

      if (
        !shouldAttemptClinicalRelease({
          status: snapshot.status,
          customerCheckoutSubmitted: snapshot.customerCheckoutSubmitted,
        })
      ) {
        return
      }

      if (releaseHoldInFlightForId === snapshot.consultationId && releaseHoldInFlight) {
        return releaseHoldInFlight
      }

      const cancelPromise = (async () => {
        activeReleaseCount.value += 1
        try {
          const cancelArgs = {
            consultationId: snapshot.consultationId as ConsultationId,
            reason: snapshot.reason,
          }
          await invokePlatformCoord('cancelClinicalConsultation', cancelArgs, () =>
            platformCoordApi.cancelClinicalConsultation(cancelArgs),
          )
          optimisticConsultationPatch.value = {
            ...(optimisticConsultationPatch.value ?? {}),
            lifecycleStatus: 'pending_cancellation',
          }
          authoritativeServerLifecycleStatus.value = 'pending_cancellation'
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          if (/cannot cancel from status|already pending cancellation/i.test(message)) {
            logger.warn('Skipped stale clinical consultation cancel', {
              consultationId: snapshot.consultationId,
              err,
            })
            return
          }
          logger.warn('Failed to cancel clinical consultation', { err })
        } finally {
          activeReleaseCount.value -= 1
          if (releaseHoldInFlightForId === snapshot.consultationId) {
            releaseHoldInFlightForId = null
            releaseHoldInFlight = null
          }
        }
      })()

      releaseHoldInFlightForId = snapshot.consultationId
      releaseHoldInFlight = cancelPromise
      return cancelPromise
    }

    async function releaseIfReserved(options?: { customerCheckoutSubmitted?: boolean }) {
      await cancelClinicalHoldFromSnapshot(
        captureCancelHoldSnapshot(options?.customerCheckoutSubmitted, 'customer_aborted'),
      )
    }

    function onCustomerSettlementCheckoutComplete() {
      if (consultationId.value) {
        markClinicalCheckoutSubmitted(consultationId.value)
      }
      customerCheckoutAcknowledged.value = true
      toastStore.debug('Settlement payment received. Thank you.')
    }

    function onCustomerSettlementCheckoutAlreadyPaid() {
      if (consultationId.value) {
        markClinicalCheckoutSubmitted(consultationId.value)
      }
      customerCheckoutPropagationError.value = null
      customerCheckoutAcknowledged.value = true
      customerAwaitingCheckoutPropagation.value = true
      clearCheckoutPropagationTimer()
      checkoutPropagationTimeoutId = window.setTimeout(() => {
        checkoutPropagationTimeoutId = null
        if (settlementPaymentComplete.value) {
          customerAwaitingCheckoutPropagation.value = false
          return
        }
        customerAwaitingCheckoutPropagation.value = false
        customerCheckoutPropagationError.value =
          'We received your payment but processing is taking longer than expected. Please close this window, wait a minute, and open your consultation again. If the balance is still due, contact support.'
      }, CUSTOMER_CHECKOUT_PROPAGATION_WAIT_MS)
    }

    function onCustomerCheckoutComplete() {
      if (consultationId.value) {
        markClinicalCheckoutSubmitted(consultationId.value)
      }
      customerCheckoutAcknowledged.value = true
      optimisticConsultationPatch.value = null
      toastStore.debug('Payment received. Your clinical consultation is scheduled.')
    }

    function onCustomerCheckoutAlreadyPaid() {
      if (consultationId.value) {
        markClinicalCheckoutSubmitted(consultationId.value)
      }
      customerCheckoutPropagationError.value = null
      customerCheckoutAcknowledged.value = true
      customerAwaitingCheckoutPropagation.value = true
      clearCheckoutPropagationTimer()
      checkoutPropagationTimeoutId = window.setTimeout(() => {
        checkoutPropagationTimeoutId = null
        if (paymentComplete.value) {
          return
        }
        customerAwaitingCheckoutPropagation.value = false
        customerCheckoutPropagationError.value =
          'We received your payment but scheduling is taking longer than expected. Please close this window, wait a minute, and open your consultation again. If it is still not scheduled, contact support.'
      }, CUSTOMER_CHECKOUT_PROPAGATION_WAIT_MS)
    }

    function onCustomerCheckoutError(message: string) {
      toastStore.error(message)
    }

    async function retryAvailability() {
      availabilityError.value = null
      slotPickerVisitError.value = null
      if (licensedStatesLoadFailed.value) {
        licensedStatesLoadFailed.value = false
        licensedStatesReady = null
        void ensureLicensedStatesLoaded()
      }
      if (isSlotPickerActive.value) {
        await availabilityStore.forceResubscribe()
        await runSlotPickerVisit({ force: true })
      } else {
        await startAvailabilityBootstrap({ ping: true })
      }
      if (!availabilityError.value) {
        error.value = null
      }
    }

    function resetLocalPicks() {
      clearLocalDraftPicksWithoutWrite()
      error.value = null
      availabilityError.value = null
      customerCheckoutAcknowledged.value = false
      clearCheckoutPropagationState()
      optimisticConsultationPatch.value = null
      reservedSubPhase.value = null
      resetInsuredCoverageState()
      resetProductPreflowState()
      ensureClinicalVendorsPromise = null
      ensureClinicalVendorsCustomerId = null
      ensureClinicalVendorsAttemptStatus = 'none'
      insuredDraftCreate = null
    }

    function captureSessionResetToken(): number {
      resetTokenSessionAttachEpoch = sessionAttachEpoch
      return operationGeneration
    }

    function signOut() {
      operationGeneration += 1
      activeSessionKey = null
      availabilityStore.endSlotPickerVisit()
      unsubscribeConsultationSnapshot()
      unsubscribeLicensedStates()
      pendingHubFollowupBooking = null
      insuredDraftCreate = null
      serverConsultation.value = null
      optimisticConsultationPatch.value = null
      clinicalHoldCapReached.value = false
      authoritativeServerLifecycleStatus.value = null
      consultationId.value = null
      isSessionHydrating.value = false
      preferredConsultationId.value = null
      sessionConsultType.value = null
      consultTypeChosen.value = null
      followupBookingEligible.value = false
      preferredFollowupTellescopeUserId.value = null
      followupBookingContextLoaded = false
      customer.value = null
      resetLocalPicks()
      cancelledAppointmentSummary.value = null
      resetClinicalBookingClientCaches()
    }

    function applyResetSessionStoreClear(customerIdForPolicy: CustomerId | undefined) {
      ++operationGeneration
      activeSessionKey = null
      unsubscribeConsultationSnapshot()
      serverConsultation.value = null
      optimisticConsultationPatch.value = null
      clinicalHoldCapReached.value = false
      authoritativeServerLifecycleStatus.value = null
      consultationId.value = null
      isSessionHydrating.value = false
      preferredConsultationId.value = null
      sessionConsultType.value = null
      consultTypeChosen.value = null
      followupBookingEligible.value = false
      preferredFollowupTellescopeUserId.value = null
      followupBookingContextLoaded = false
      pendingHubFollowupBooking = null
      if (customerIdForPolicy) {
        invalidateReusableBridgePolicyPrefetch(customerIdForPolicy)
      }
      customer.value = null
      unsubscribeLicensedStates()
      resetLocalPicks()
      cancelledAppointmentSummary.value = null
      availabilityStore.endSlotPickerVisit()
    }

    async function resetSession(options?: { expectedGeneration?: number }) {
      if (
        options?.expectedGeneration != null &&
        options.expectedGeneration !== operationGeneration
      ) {
        return
      }
      if (
        options?.expectedGeneration != null &&
        resetTokenSessionAttachEpoch !== sessionAttachEpoch
      ) {
        return
      }

      const customerIdForPolicy = customer.value?.id as CustomerId | undefined
      const customerIdForDraft = customer.value?.id
      const releaseSnapshot = captureCancelHoldSnapshot(undefined, 'customer_aborted')

      applyResetSessionStoreClear(customerIdForPolicy)

      isResettingSession.value = true

      const backgroundWork = Promise.resolve().then(async () => {
        try {
          await cancelClinicalHoldFromSnapshot(releaseSnapshot)
        } catch (err) {
          logger.error('Failed to cancel clinical hold on reset', { err })
        }
        if (customerIdForDraft) {
          void availabilityStore.unmarkSlotAsSelected(customerIdForDraft).catch((err) => {
            logger.error('Failed to unmark clinical draft on reset', {
              customerId: customerIdForDraft,
              err,
            })
          })
        }
      })

      const resetTail = resetSessionInFlight
        ? resetSessionInFlight.then(() => backgroundWork)
        : backgroundWork

      resetSessionInFlight = resetTail
      void resetTail.finally(() => {
        if (resetSessionInFlight === resetTail) {
          resetSessionInFlight = null
          isResettingSession.value = false
        }
      })

      return resetSessionInFlight
    }

    watch(entrySource, (entrySourceArg) => {
      toastStore.debug(`Booking Entry source changed to : '${entrySourceArg}'`)
    })

    watch(isPlatformCoordBusy, (isPlatformCoordBusyArg) => {
      toastStore.debug(`Platform coord busy changed to : '${isPlatformCoordBusyArg}'`)
    })

    watch(isResettingSession, (isResettingSessionArg) => {
      toastStore.debug(`Resetting session changed to : '${isResettingSessionArg}'`)
    })

    watch(consultation, (consultationArg) => {
      const lifecycleStatus = consultationArg?.lifecycleStatus || 'unknown'
      toastStore.debug(`Consultation lifecycle status changed to : '${lifecycleStatus}'`)
    })

    watch(
      consultationId,
      (id) => {
        setBookingTrackingConsultationId(id)
      },
      { immediate: true },
    )

    return {
      customer,
      preferredConsultationId,
      consultTypeLocked,
      consultTypeChosen,
      consultation,
      consultationId,
      selectedDate,
      selectedSlot,
      userPickedSlot,
      isDraftSlotReady,
      activeBridgePolicyId,
      slotSelectionError,
      slotSelectionErrorSlotId,
      error,
      isReserving,
      isMarkingDraftSlot,
      isConfirming,
      isReleasingReservation,
      isResettingSession,
      isPlatformCoordBusy,
      isConsultationLifecycleStale: isConsultationLifecycleStaleComputed,
      isBookingInteractionLocked,
      isAvailabilityLoading,
      isCalendarSlotsReady,
      isSlotPickerActive,
      availabilityError,
      isConsultationSnapshotLoading,
      isSessionHydrating,
      paymentComplete,
      settlementPaymentComplete,
      payStepStripeCheckoutPaid,
      customerCheckoutAcknowledged,
      customerAwaitingCheckoutPropagation,
      customerCheckoutPropagationError,
      checkoutExpiresAt,
      clinicalHoldCapReached,
      isClinicalHoldActive,
      holdMinutesRemaining,
      holdDisplayMessage,
      currentStep,
      draftSubPhase,
      isInsuredPath,
      billingPath,
      insuredVisitStateForBridge,
      clinicalBookingVisitState,
      clinicalBookingTimezone,
      isWaitingForInsuredCoverage,
      insuredCoverageError,
      insuredCoveragePhase,
      insuredCoveragePatientResponsibility,
      insuredCoveragePatientResponsibilityBreakdown,
      insuredSettlementOutstandingCents,
      insuredSettlementPatientResponsibilityBreakdown,
      reservedSubPhase,
      productStep,
      productPreflowComplete,
      insuranceCategory,
      cashPayEligibleReason,
      insuredPayerDisplayName,
      insuredPolicySharedAt,
      hasScheduledConsultation,
      scheduledConsultation,
      cancelledAppointmentSummary,
      reusableBridgePolicyId,
      insuranceDetailsEntry,
      chargeFullListPrice,
      insuredPaymentFailureReason,
      profileDraft,
      activeConsultType,
      effectiveBookingConsultType,
      followupBookingEligible,
      followupInsurancePolicyLocked,
      preferredFollowupTellescopeUserId,
      entrySource,
      licensedStatesCacheLoaded,
      userIdToConsultantDisplayNameFromCache,
      supportedStateCodes,
      subscribeLicensedStates,
      ensureLicensedStatesLoaded,
      unsubscribeLicensedStates,
      isProfileStateSupported,
      startSession,
      setConsultType,
      setBillingPath,
      createClinicalConsultation,
      finishInsuredInsuranceSetup,
      confirmInsuranceOnFile,
      clearInsuranceDetailsEntry,
      declineInsuranceOnFile,
      completeInsuranceVerifiedStep,
      cancelScheduledConsultation,
      bookAgainAfterCancellation,
      completeIntro,
      completeProfile,
      selectInsuranceCategory,
      confirmCashPayEligible,
      handleUnsupportedState,
      syncProfileDraftStateFromConfirm,
      runHardVerificationAndContinue,
      completeClinicalInsuranceVerifyAsSelfPay,
      completeInsuranceVerify,
      clearConsultTypeChoice,
      selectDate,
      selectSlot,
      reserveSlot,
      extendClinicalHold,
      waitForInsuredCoverageProjection,
      switchReservedToSelfPay,
      confirmBooking,
      goBackToDraft,
      releaseIfReserved,
      onCustomerCheckoutComplete,
      onCustomerSettlementCheckoutComplete,
      onCustomerSettlementCheckoutAlreadyPaid,
      onCustomerCheckoutAlreadyPaid,
      onCustomerCheckoutError,
      retryAvailability,
      resetSession,
      captureSessionResetToken,
      signOut,
      resolveResumableClinicalConsultation,
      prepareExistingConsultationGateView,
      stageFollowupBookingFromHub,
    }
  },
)
